import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpClientModule, HttpErrorResponse } from '@angular/common/http';
import { TradeEntry, TradeDirection, TradeState, TradeCurrency, PartialSale, TradeScreenshot } from '../../models/trade-entry';
import { ApiService } from '../../services/api.service';
import { AiPromptTemplate } from '../../models/ai-prompt';
import { v4 as uuid } from 'uuid';
import { Subscription, firstValueFrom } from 'rxjs';
import { OpenAiService } from '../../services/openai.service';
import { RiskBudgetService, RiskSummary } from '../../services/risk-budget.service';
import { DEFAULT_RISK_PROFILE, RiskStep } from '../../models/risk-setup';
import { FieldText } from '../../models/field';
import { AnalysisDialogComponent, AnalysisFile, AnalysisScenarioCandidate, AnalysisVariant } from '../../components/analysis-dialog/analysis-dialog.component';

interface FieldTextImage {
  id: string;
  name: string;
  imageDataUrl: string;
}

interface FieldTextLink {
  id: string;
  label: string;
  url: string;
}

@Component({
  selector: 'app-journal',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, HttpClientModule, AnalysisDialogComponent],
  templateUrl: './journal.component.html',
  styleUrls: ['./journal.component.scss']
})
export class JournalComponent implements OnInit, OnDestroy {
  tradeForm: FormGroup;
  sellForm: FormGroup;
  positionForm: FormGroup;
  dailyCheckForm: FormGroup;
  entryQuestions: Array<{ key: string }> = [
    { key: 'questionHigherTimeframe' },
    { key: 'questionHigherTimeframeConfirmsTrend' },
    { key: 'questionSupportResistance' },
    { key: 'questionEma200' },
    { key: 'questionEarningsSoon' },
    { key: 'questionSetupConfidence' },
    { key: 'questionMotivation' },
    { key: 'questionEmotionalState' },
    { key: 'questionRiskManagement' },
    { key: 'questionLastTradesPositive' },
    { key: 'questionReviewedLastTrades' },
    { key: 'questionRsiUnder60' },
    { key: 'questionRsiOverbought' },
    { key: 'questionTrendClear' },
    { key: 'questionSignalCandle' },
    { key: 'questionVolumeRising' },
    { key: 'questionRecommendedStock' },
    { key: 'questionHighlySpeculative' },
    { key: 'questionLowVolumeMoves' }
  ];
  private questionTextMap: Record<string, { text?: string; long?: string; short?: string }> = {
    questionHigherTimeframe: {
      long: 'Ist der Trend klar aufwärts?',
      short: 'Ist der Trend klar abwärts?'
    },
    questionHigherTimeframeConfirmsTrend: { text: 'Bestätigt der Übergeordnete Timeframe den Trend?' },
    questionSupportResistance: {
      long: 'Kurs befindet sich an einer Unterstützung',
      short: 'Kurs befindet sich an einem Widerstand'
    },
    questionEma200: {
      long: 'Ist der Kurs über dem EMA 200',
      short: 'Ist der Kurs unter dem EMA 200'
    },
    questionEarningsSoon: { text: 'Stehen Earnings an?' },
    questionSetupConfidence: { text: 'Kann ich in 3 sec. ja zu meinem Setup und dem Chart sagen?' },
    questionMotivation: { text: 'Mache ich den Trade um Geld zu verdienen oder Weil mein Ego das will?' },
    questionEmotionalState: { text: 'Emmotional bin ich drauf:' },
    questionRiskManagement: {
      text: 'Risk Management check. Ist meine Entscheidung auf Struktur begründet? Oder Angst? Ist dein Stop optimal oder zu optimistisch?'
    },
    questionLastTradesPositive: { text: 'War einer der letzten drei Trades positiv?' },
    questionReviewedLastTrades: { text: 'Habe ich mir die drei Trades nochmal angeschaut?' },
    questionRsiUnder60: {
      long: 'Ist der RSI unter 60?',
      short: 'Ist der RSI über 70?'
    },
    questionRsiOverbought: {
      long: 'Ist der RSI überkauft?',
      short: 'Ist der RSI überverkauft?'
    },
    questionTrendClear: {
      long: 'Ist der Trend klar aufwärts?',
      short: 'Ist der Trend klar abwärts?'
    },
    questionSignalCandle: { text: 'Ist die letzte Kerze eine Signalkerze?' },
    questionVolumeRising: { text: 'Ist das Volumen abnehmend bei den letzten Kerzen?' },
    questionRecommendedStock: { text: 'Wurde die Aktie von jemandem empfohlen?' },
    questionHighlySpeculative: { text: 'Handelt es sich um eine sehr spekulative Aktie?' },
    questionLowVolumeMoves: { text: 'Kann die Aktie mit sehr wenig Volumen starke Kursveränderungen zeigen?' }
  };
  setups = ['Ausbruch', 'Trend', 'Ground', 'Squeeze'];
  setupDetails: FieldText[] = [];
  cfdDetails: FieldText[] = [];
  currencies: TradeCurrency[] = ['EUR', 'USD'];
  journalAreas: FieldText[] = [];
  activeJournalArea: FieldText | null = null;
  entries = signal<TradeEntry[]>([]);
  sellDialogEntryId: string | null = null;
  sellDialogMode: 'sell' | 'position' | null = null;
  stornoDialogEntryId: string | null = null;
  stornoScreenshotUrl = '';
  stornoScreenshotUploadName = '';
  stornoScreenshotUploadError = '';
  stornoScreenshotDropActive = false;
  private stornoScreenshotUploadDataUrl = '';
  dailyCheckEntryId: string | null = null;
  editingEntryId: string | null = null;
  evaluationEntryId: string | null = null;
  evaluationComment = '';
  activeEvaluationMediaId = '';
  setupDetailText: FieldText | null = null;
  activeSetupDetailTabId = '';
  evaluationMediaUrl = '';
  evaluationMediaUploadName = '';
  evaluationMediaUploadError = '';
  evaluationMediaDropActive = false;
  private evaluationMediaUploadDataUrl = '';
  editingValues: { entryPrice: number; stopPrice: number; targetPrice: number; size: number; entryFee: number; comment: string; stopChangeReason: string } = {
    entryPrice: 0,
    stopPrice: 0,
    targetPrice: 0,
    size: 0,
    entryFee: 0,
    comment: '',
    stopChangeReason: ''
  };
  commentTooltipText = '';
  commentTooltipX = 0;
  commentTooltipY = 0;
  aiStatus = '';
  showFinancialColumns = false;
  showAiAndJsonInputs = false;
  pageSizeOptions = [10, 20, 50];
  pageSize = 20;
  currentPage = 1;
  sortField: 'sellDate' | 'symbol' | 'entryDate' | 'status' = 'entryDate';
  sortDirection: 'asc' | 'desc' = 'desc';
  watchStatusFilter: 'open' | 'storno' | 'all' = 'open';
  watchPageSize = 20;
  watchCurrentPage = 1;
  watchSortField: 'sellDate' | 'symbol' | 'entryDate' | 'status' = 'entryDate';
  watchSortDirection: 'asc' | 'desc' = 'desc';
  yearFilterOptions: string[] = [];
  yearFilter = '';
  dateFrom = '';
  dateTo = '';
  positionedStatusFilter: '' | 'Positioniert' | 'Teilverkauf' | 'Glatt' = '';
  positionedPnLEuro = 0;
  totalRiskBudgetEuro = 0;
  riskPerTradeEuro = 0;
  riskRewardRatio = DEFAULT_RISK_PROFILE.riskRewardRatio ?? 0;
  appliedRiskStep?: RiskStep;
  riskPanelCollapsed = true;
  chatPanelCollapsed = true;
  promptPanelVisible = false;
  entryPanelCollapsed = false;
  setupQuestionsCollapsed = true;
  emotionQuestionsCollapsed = true;
  technicalQuestionsCollapsed = true;
  aiPrompts: AiPromptTemplate[] = [];
  editPromptName = '';
  selectedPromptId: string | null = null;
  newPromptName = '';
  partialToggles = new Set<string>();
  screenshotToggles = new Set<string>();
  analysisDropActive = false;
  analysisDialogOpen = false;
  analysisPersistedId = '';
  analysisRawJson = '';
  analysisFileName = '';
  analysisUploadError = '';
  analysisImageDropActive = false;
  analysisImageUploadName = '';
  analysisImageRunning = false;
  analysisData: AnalysisFile | null = null;
  private selectedAnalysisVariant: AnalysisVariant | null = null;
  private selectedAnalysisVariantIndex: number | null = null;
  screenshotDropActive = false;
  screenshotUploadName = '';
  screenshotUploadError = '';
  private screenshotUploadDataUrl = '';
  private cfdEntryFeeManuallyEdited = false;
  exchangeRates: Record<TradeCurrency, number> = { EUR: 1, USD: 1 };
  warningMessage = '';
  errorMessage = '';
  confirmMessage = '';
  confirmAction: (() => void | Promise<void>) | null = null;
  private subscriptions = new Subscription();
  private manualComment = '';
  private commentSyncActive = false;
  private commentSyncFn: (() => void) | null = null;
  private dailyCheckBaseComment = '';
  private readonly takeProfitCommentPrompt = 'Was darf meinen Take Profit verändern?';

  constructor(
    private fb: FormBuilder,
    private api: ApiService,
    private openAi: OpenAiService,
    private riskBudget: RiskBudgetService
  ) {
    this.tradeForm = this.fb.group({
      symbol: ['', Validators.required],
      wknIsin: [
        '',
        [Validators.pattern(/^([A-Z]{2}[A-Z0-9]{9}[0-9]|[A-Z0-9]{6})$/i)]
      ],
      positioned: [false],
      entryDate: [''],
      sellDate: [''],
      direction: ['Long' as TradeDirection, Validators.required],
      certificate: [false],
      tradeType: ['CFD'],
      setup: ['Ausbruch', Validators.required],
      currency: ['EUR' as TradeCurrency, Validators.required],
      crvGrund: [3, [Validators.min(0)]],
      marge: [0, [Validators.min(0)]],
      entryPrice: [0, [Validators.required, Validators.min(0.01)]],
      stopPrice: [0, [Validators.required, Validators.min(0.01)]],
      targetPrice: [0, [Validators.required, Validators.min(0.01)]],
      size: [0, [Validators.required, Validators.min(1)]],
      entryFee: [0, [Validators.min(0)]],
      aiPrompt: [''],
      aiSystemPrompt: [''],
      questionHigherTimeframe: [null],
      questionHigherTimeframeConfirmsTrend: [null],
      questionSupportResistance: [null],
      questionEarningsSoon: [null],
      questionSetupConfidence: [null],
      questionMotivation: [null],
      questionEmotionalState: [''],
      questionRiskManagement: [null],
      questionLastTradesPositive: [null],
      questionReviewedLastTrades: [null],
      questionEma200: [null],
      questionRsiUnder60: [null],
      questionRsiOverbought: [null],
      questionTrendClear: [null],
      questionSignalCandle: [null],
      questionVolumeRising: [null],
      questionRecommendedStock: [null],
      questionHighlySpeculative: [null],
      questionLowVolumeMoves: [null],
      comment: ['', Validators.required],
      screenshotUrl: ['']
    });

    this.sellForm = this.fb.group({
      sellPrice: [0, [Validators.required, Validators.min(0)]],
      sellSize: [0, [Validators.required, Validators.min(0)]],
      sellDate: ['', Validators.required],
      sellScreenshotUrl: [''],
      sellFee: [0, [Validators.min(0)]],
      sellComment: [''],
      rulesFollowed: [null, Validators.required],
      planFollowed: [null],
      entryWasValid: [null],
      exitWasValid: [null],
      stopWasRespected: [null],
      positionSizeWasCorrect: [null],
      ruleBroken: [null]
    });

    this.positionForm = this.fb.group({
      entryPrice: [0, [Validators.required, Validators.min(0.01)]],
      entryDate: ['', Validators.required],
      comment: ['']
    });

    this.dailyCheckForm = this.fb.group({
      structureIntact: [null, Validators.required],
      assumptionViolated: [null, Validators.required],
      stopCorrect: [null, Validators.required],
      earningsSoon: [null, Validators.required],
      rulesFollowed: [null, Validators.required],
      longTermTrendIntact: [null],
      longTermRsiExtreme: [null],
      longTermStopCorrect: [null],
      comment: [''],
    });
  }

  averageSellPrice(entry: TradeEntry): number | null {
    const partials = entry.partialSales ?? [];
    if (partials.length > 0) {
      const totals = partials.reduce(
        (acc, sale) => {
          acc.totalSize += sale.size;
          acc.totalValue += sale.price * sale.size;
          return acc;
        },
        { totalSize: 0, totalValue: 0 }
      );

      if (totals.totalSize > 0) {
        return +(totals.totalValue / totals.totalSize).toFixed(2);
      }
    }

    if (entry.sellPrice !== undefined && entry.sellSize) {
      return +entry.sellPrice.toFixed(2);
    }

    return entry.sellPrice !== undefined ? +entry.sellPrice.toFixed(2) : null;
  }

  get sellEntry(): TradeEntry | undefined {
    return this.sellDialogEntryId
      ? this.entries().find((entry) => entry.id === this.sellDialogEntryId)
      : undefined;
  }

  get stornoEntryForDialog(): TradeEntry | undefined {
    return this.stornoDialogEntryId
      ? this.entries().find((entry) => entry.id === this.stornoDialogEntryId)
      : undefined;
  }

  get evaluationEntry(): TradeEntry | undefined {
    return this.evaluationEntryId
      ? this.entries().find((entry) => entry.id === this.evaluationEntryId)
      : undefined;
  }

  openEvaluationDialog(entry: TradeEntry): void {
    if (!this.canOpenEvaluation(entry)) return;
    this.evaluationEntryId = entry.id;
    this.evaluationComment = entry.comment ?? '';
    this.activeEvaluationMediaId = this.evaluationMediaTabs(entry)[0]?.id || '';
    this.clearEvaluationMediaForm();
  }

  closeEvaluationDialog(): void {
    this.evaluationEntryId = null;
    this.evaluationComment = '';
    this.activeEvaluationMediaId = '';
    this.clearEvaluationMediaForm();
  }

  canOpenEvaluation(entry: TradeEntry): boolean {
    return entry.status === 'Glatt';
  }

  evaluationMediaTabs(entry: TradeEntry): Array<{ id: string; label: string; url: string; type: 'link' | 'image' }> {
    return this.screenshotLines(entry).map((line, index) => ({
      id: `media-${index}`,
      ...line
    }));
  }

  activeEvaluationMedia(entry: TradeEntry): { id: string; label: string; url: string; type: 'link' | 'image' } | null {
    const tabs = this.evaluationMediaTabs(entry);
    return tabs.find((tab) => tab.id === this.activeEvaluationMediaId) ?? tabs[0] ?? null;
  }

  setActiveEvaluationMedia(id: string): void {
    this.activeEvaluationMediaId = id;
  }

  currentSetupDetail(): FieldText | null {
    const setup = this.tradeForm?.get('setup')?.value?.toString();
    if (!setup) return null;
    return this.setupDetails.find((item) => item.bezeichnung === setup) ?? null;
  }

  openSetupDetails(): void {
    const detail = this.currentSetupDetail();
    if (!detail) return;
    this.setupDetailText = detail;
    this.activeSetupDetailTabId = this.setupDetailTabs(detail)[0]?.id || '';
  }

  closeSetupDetails(): void {
    this.setupDetailText = null;
    this.activeSetupDetailTabId = '';
  }

  hasSetupDetails(): boolean {
    const detail = this.currentSetupDetail();
    return !!detail && (!!detail.beschreibung?.trim() || this.parseFieldTextImages(detail).length > 0 || this.parseFieldTextLinks(detail).length > 0);
  }

  setupDetailTabs(fieldText = this.setupDetailText): Array<{ id: string; label: string; type: 'image' | 'links'; image?: FieldTextImage }> {
    if (!fieldText) return [];
    const imageTabs = this.parseFieldTextImages(fieldText).map((image, index) => ({
      id: image.id,
      label: image.name || `Bild ${index + 1}`,
      type: 'image' as const,
      image
    }));
    const links = this.parseFieldTextLinks(fieldText);
    return links.length
      ? [...imageTabs, { id: 'links', label: `Links (${links.length})`, type: 'links' as const }]
      : imageTabs;
  }

  activeSetupDetailTab(): { id: string; label: string; type: 'image' | 'links'; image?: FieldTextImage } | null {
    const tabs = this.setupDetailTabs();
    return tabs.find((tab) => tab.id === this.activeSetupDetailTabId) ?? tabs[0] ?? null;
  }

  setActiveSetupDetailTab(id: string): void {
    this.activeSetupDetailTabId = id;
  }

  setupDetailLinks(): FieldTextLink[] {
    return this.setupDetailText ? this.parseFieldTextLinks(this.setupDetailText) : [];
  }

  onSetupSelectionChange(): void {
    this.applySetupDefaults(this.tradeForm.get('setup')?.value);
    this.recalculatePositionAndProfit('risk');
  }

  onTradeTypeChange(): void {
    const tradeType = this.tradeForm.get('tradeType')?.value;
    const certificate = this.isCertificateType(tradeType);
    this.tradeForm.get('certificate')?.setValue(certificate, { emitEvent: false });
    if (this.isCfdType(tradeType)) {
      this.cfdEntryFeeManuallyEdited = false;
      const currentSymbol = this.tradeForm.get('symbol')?.value?.toString();
      if (!currentSymbol || !this.cfdDetails.some((item) => item.bezeichnung === currentSymbol)) {
        this.tradeForm.get('symbol')?.setValue(this.cfdDetails[0]?.bezeichnung ?? '', { emitEvent: false });
      }
      this.applyCfdDefaults();
    } else {
      this.tradeForm.get('marge')?.setValue(0, { emitEvent: false });
      this.applySetupDefaults(this.tradeForm.get('setup')?.value);
    }
    this.recalculatePositionAndProfit('direction');
  }

  onCfdValueChange(): void {
    this.cfdEntryFeeManuallyEdited = false;
    this.applyCfdDefaults();
  }

  markCfdEntryFeeManual(): void {
    if (this.isCfdSelected()) {
      this.cfdEntryFeeManuallyEdited = true;
    }
  }

  onCfdTargetChange(): void {
    this.applyCfdStopFromTarget();
    this.recalculatePositionAndProfit(this.isCfdSelected() ? 'risk' : 'target');
  }

  isCfdSelected(): boolean {
    return this.isCfdType(this.tradeForm.get('tradeType')?.value);
  }

  private applySetupDefaults(setupValue: unknown): void {
    if (this.isCfdSelected()) return;
    const setup = setupValue?.toString();
    const detail = setup ? this.setupDetails.find((item) => item.bezeichnung === setup) : null;
    const crvGrund = this.validPositiveNumber(detail?.crvGrund) ?? 3;
    const marge = this.validNumber(detail?.marge) ?? 0;
    this.tradeForm.patchValue({ crvGrund, marge }, { emitEvent: false });
  }

  private applyCfdDefaults(): void {
    const cfd = this.selectedCfdDetail();
    const crvGrund = this.validPositiveNumber(cfd?.crvGrund) ?? 3;
    const marge = this.validPositiveNumber(cfd?.marge) ?? 0;
    this.tradeForm.patchValue({ crvGrund, marge }, { emitEvent: false });
    this.applyCfdStopFromTarget();
    this.recalculatePositionAndProfit('risk');
  }

  private selectedCfdDetail(): FieldText | undefined {
    const symbol = this.tradeForm.get('symbol')?.value?.toString();
    return this.cfdDetails.find((item) => item.bezeichnung === symbol);
  }

  private selectedCrvRatio(): number {
    return this.validPositiveNumber(this.tradeForm.value.crvGrund) ?? 3;
  }

  private applyCfdStopFromTarget(): void {
    if (!this.isCfdSelected()) return;

    const entry = Number(this.tradeForm.value.entryPrice ?? 0);
    const target = Number(this.tradeForm.value.targetPrice ?? 0);
    const ratio = this.selectedCrvRatio();
    const direction = this.calculationDirectionForForm();
    if (entry <= 0 || target <= 0 || ratio <= 0) return;

    const reward = direction === 'Long' ? target - entry : entry - target;
    if (reward <= 0) return;

    const stop = direction === 'Long'
      ? entry - (reward / ratio)
      : entry + (reward / ratio);
    this.tradeForm.get('stopPrice')?.setValue(+stop.toFixed(2), { emitEvent: false });
  }

  private isCertificateType(value: unknown): boolean {
    return value?.toString() === 'Zertifikat';
  }

  private isCfdType(value: unknown): boolean {
    return value?.toString() === 'CFD';
  }

  private validPositiveNumber(value: unknown): number | null {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) && numberValue > 0 ? numberValue : null;
  }

  private validNumber(value: unknown): number | null {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : null;
  }

  onEvaluationMediaDragOver(event: DragEvent): void {
    event.preventDefault();
    this.evaluationMediaDropActive = true;
  }

  onEvaluationMediaDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.evaluationMediaDropActive = false;
  }

  handleEvaluationMediaDrop(event: DragEvent): void {
    event.preventDefault();
    this.evaluationMediaDropActive = false;
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this.processEvaluationMediaFile(file);
    }
  }

  handleEvaluationMediaFileSelection(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.processEvaluationMediaFile(file);
      input.value = '';
    }
  }

  handleEvaluationMediaPaste(event: ClipboardEvent): void {
    const file = this.imageFileFromClipboard(event);
    if (file) {
      event.preventDefault();
      this.processEvaluationMediaFile(file);
    }
  }

  clearEvaluationMediaUpload(): void {
    this.evaluationMediaUploadName = '';
    this.evaluationMediaUploadError = '';
    this.evaluationMediaUploadDataUrl = '';
  }

  async saveEvaluationDetails(entry: TradeEntry): Promise<void> {
    const screenshots: TradeScreenshot[] = [...(entry.screenshots ?? [])];
    const lastSaleId = (entry.partialSales ?? [])[entry.partialSales?.length ? entry.partialSales.length - 1 : -1]?.id;
    const trimmedUrl = this.evaluationMediaUrl.trim();
    if (trimmedUrl) {
      screenshots.push({ id: uuid(), url: trimmedUrl, indexOrder: screenshots.length, partialSaleId: lastSaleId });
    }
    if (this.evaluationMediaUploadDataUrl) {
      screenshots.push({ id: uuid(), imageDataUrl: this.evaluationMediaUploadDataUrl, indexOrder: screenshots.length, partialSaleId: lastSaleId });
    }

    const changed: TradeEntry = {
      ...entry,
      comment: this.evaluationComment.trim(),
      screenshots,
    };

    try {
      const saved = await this.saveEntry(changed);
      this.entries.set(this.entries().map((item) => item.id === saved.id ? saved : item));
      this.evaluationEntryId = saved.id;
      const tabs = this.evaluationMediaTabs(saved);
      this.activeEvaluationMediaId = tabs[tabs.length - 1]?.id || this.activeEvaluationMediaId;
      this.clearEvaluationMediaForm();
    } catch {
      // Fehler wurde bereits angezeigt
    }
  }

  private async loadPrompts(): Promise<void> {
    try {
      const templates = await firstValueFrom(this.api.getAiPromptTemplates());
      this.aiPrompts = templates ?? [];

      if (this.aiPrompts.length) {
        const first = this.aiPrompts[0];
        this.selectedPromptId = first.id;
        this.editPromptName = first.name;
        this.tradeForm.patchValue({ aiSystemPrompt: first.systemPrompt || first.text });
      }
    } catch (error) {
      // keep prompts empty when backend is unavailable
    }
  }

  private async fetchTrades(): Promise<void> {
    try {
      const remote = await firstValueFrom(this.api.getTrades(this.activeJournalArea?.feldTextId));
      const normalized = (remote ?? [])
        .map((entry) => this.normalizeEntry(entry));
      this.entries.set(normalized);
      this.updatePositionedPnl();
      this.yearFilterOptions = this.buildYearOptions();
      this.ensurePageWithinBounds();
      this.ensureWatchPageWithinBounds();
    } catch (error) {
      this.entries.set([]);
    }
  }

  async setActiveJournalArea(area: FieldText): Promise<void> {
    if (this.activeJournalArea?.feldTextId === area.feldTextId) return;
    this.activeJournalArea = area;
    this.syncConditionalQuestions();
    this.commentSyncFn?.();
    this.sellDialogEntryId = null;
    this.sellDialogMode = null;
    this.dailyCheckEntryId = null;
    this.editingEntryId = null;
    this.currentPage = 1;
    this.watchCurrentPage = 1;
    await this.fetchTrades();
  }

  private defaultArea(): FieldText {
    return this.journalAreas[0] ?? { feldTextId: 'swingetrades', bezeichnung: 'Swingetrades' };
  }

  private async loadJournalAreas(): Promise<void> {
    const fieldCandidates = ['trade_grouppe', 'trade_gruppe'];

    for (const fieldId of fieldCandidates) {
      try {
        const texts = await firstValueFrom(this.api.getFieldTexts(fieldId));
        const areas = (texts ?? [])
          .map((item) => ({
            anzeigen: item.anzeigen,
            feldTextId: item.feldTextId?.toString().trim(),
            bezeichnung: item.bezeichnung?.toString().trim(),
            position: item.position,
          }))
          .filter((value) => value.anzeigen !== false)
          .filter((value) => !!value.feldTextId && !!value.bezeichnung)
          .map((value) => ({
            feldTextId: value.feldTextId as string,
            bezeichnung: value.bezeichnung as string,
            position: value.position,
          }))
          .sort((a, b) => (a.position ?? Number.MAX_SAFE_INTEGER) - (b.position ?? Number.MAX_SAFE_INTEGER));

        if (areas.length) {
          this.journalAreas = areas;
          this.activeJournalArea = areas[0];
          return;
        }
      } catch {
        // try next fallback field id
      }
    }

    this.journalAreas = [
      { feldTextId: 'swingetrades', bezeichnung: 'Swingetrades', position: 1 },
      { feldTextId: 'langfristinvestitionen', bezeichnung: 'Langfristinvestitionen', position: 2 },
    ];
    this.activeJournalArea = this.journalAreas[0];
  }

  private async loadSetups(): Promise<void> {
    const fallbackSetups = ['Ausbruch', 'Trend', 'Ground', 'Squeeze'];

    try {
      const texts = await firstValueFrom(this.api.getFieldTexts('trade_setup'));
      const detailOptions = (texts ?? [])
        .map((item) => ({
          ...item,
          feldTextId: item.feldTextId?.toString().trim() || '',
          bezeichnung: item.bezeichnung?.toString().trim(),
          position: item.position,
        }))
        .filter((value) => value.anzeigen !== false)
        .filter((value) => !!value.bezeichnung)
        .sort((a, b) => (a.position ?? Number.MAX_SAFE_INTEGER) - (b.position ?? Number.MAX_SAFE_INTEGER));

      this.setupDetails = detailOptions as FieldText[];
      this.setups = detailOptions.length ? detailOptions.map((value) => value.bezeichnung as string) : fallbackSetups;
    } catch {
      this.setupDetails = [];
      this.setups = fallbackSetups;
    }

    const setupControl = this.tradeForm.get('setup');
    if (!setupControl) return;

    const currentSetup = (setupControl.value ?? '').toString();
    if (!currentSetup || !this.setups.includes(currentSetup)) {
      setupControl.setValue(this.setups[0]);
    }
    this.applySetupDefaults(setupControl.value);
  }

  private async loadCfdValues(): Promise<void> {
    try {
      const texts = await firstValueFrom(this.api.getFieldTexts('trading_CFD'));
      this.cfdDetails = (texts ?? [])
        .map((item) => ({ ...item, bezeichnung: item.bezeichnung?.toString().trim(), position: item.position }))
        .filter((item) => item.anzeigen !== false && !!item.bezeichnung)
        .sort((a, b) => (a.position ?? Number.MAX_SAFE_INTEGER) - (b.position ?? Number.MAX_SAFE_INTEGER)) as FieldText[];
    } catch {
      this.cfdDetails = [];
    }

    if (this.isCfdSelected()) {
      this.onTradeTypeChange();
    }
  }

  private async saveEntry(entry: TradeEntry, isNew = false): Promise<TradeEntry> {
    try {
      const payload = this.withLearningMetrics(entry);
      const saved = isNew
        ? await firstValueFrom(this.api.createTrade(payload))
        : payload.id
          ? await firstValueFrom(this.api.updateTrade(payload.id, payload))
          : await firstValueFrom(this.api.createTrade(payload));
      return this.normalizeEntry(saved);
    } catch (error) {
      this.showError('Speichern des Trades ist fehlgeschlagen.', error);
      throw error;
    }
  }

  private async deleteEntryRemote(id: string): Promise<void> {
    try {
      await firstValueFrom(this.api.deleteTrade(id));
    } catch (error) {
      // ignore backend errors for local fallback
    }
  }

  async ngOnInit(): Promise<void> {
    await this.loadAppSettings();
    await this.loadSetups();
    await this.loadCfdValues();
    await this.loadJournalAreas();
    await this.fetchTrades();
    await this.loadRiskProfile();
    this.setupRiskSummarySubscription();
    this.setupQuestionnaireSync();

    await this.loadPrompts();

    const positionedControl = this.tradeForm.get('positioned');
    const entryDateControl = this.tradeForm.get('entryDate');
    const currencyControl = this.tradeForm.get('currency');

    positionedControl?.valueChanges.subscribe((value: boolean) => {
      if (value && !entryDateControl?.value) {
        entryDateControl?.setValue(this.today());
      }
    });

    if (positionedControl?.value && !entryDateControl?.value) {
      entryDateControl?.setValue(this.today());
    }

    currencyControl?.valueChanges.subscribe((value: TradeCurrency) => {
      this.loadExchangeRate(value);
    });

    if (currencyControl?.value) {
      this.loadExchangeRate(currencyControl.value as TradeCurrency);
    }

    this.loadExchangeRate('USD');

    this.setupAutoCalculations();
    this.yearFilterOptions = this.buildYearOptions();
    this.ensurePageWithinBounds();
    this.ensureWatchPageWithinBounds();
  }

  private setupQuestionnaireSync(): void {
    const commentControl = this.tradeForm.get('comment');
    if (!commentControl) return;

    const updateComment = () => {
      const questionBlock = this.buildQuestionBlock();
      const manual = this.manualComment.trim();
      const combined = [questionBlock, manual].filter((part) => !!part).join('\n\n');
      this.commentSyncActive = true;
      commentControl.setValue(combined, { emitEvent: false });
      this.commentSyncActive = false;
    };
    this.commentSyncFn = updateComment;

    commentControl.valueChanges.subscribe((value) => {
      if (this.commentSyncActive) return;
      this.manualComment = this.stripQuestionBlock(value?.toString() ?? '');
    });

    this.entryQuestions.forEach((question) => {
      this.tradeForm.get(question.key)?.valueChanges.subscribe(() => {
        updateComment();
      });
    });

    this.tradeForm.get('direction')?.valueChanges.subscribe(() => {
      this.syncConditionalQuestions();
      updateComment();
    });

    this.tradeForm.get('questionLastTradesPositive')?.valueChanges.subscribe(() => {
      this.syncConditionalQuestions();
      updateComment();
    });

    this.syncConditionalQuestions();
    updateComment();
  }

  private buildQuestionBlock(): string {
    return [
      this.takeProfitCommentPrompt,
      ...this.visibleEntryQuestions()
      .map((question) => {
        const value = this.tradeForm.get(question.key)?.value as boolean | string | null;
        const answer = this.answerLabel(value, question.key);
        return answer ? `${this.questionText(question.key)} ${answer}` : '';
      })
      .filter((line) => !!line)
    ].join('\n');
  }

  private answerLabel(value: boolean | string | null, key?: string): string {
    if (key === 'questionEmotionalState') {
      return typeof value === 'string' && value ? value : '';
    }
    if (value === true) return 'Ja';
    if (value === false) return 'Nein';
    return '';
  }

  setQuestionValue(key: string, value: boolean): void {
    const control = this.tradeForm.get(key);
    if (!control) return;
    control.setValue(value);
    this.commentSyncFn?.();
  }

  setQuestionChoice(key: string, value: string): void {
    const control = this.tradeForm.get(key);
    if (!control) return;
    control.setValue(value);
    this.commentSyncFn?.();
  }

  private stripQuestionBlock(comment: string): string {
    const normalized = (comment ?? '').toString();
    if (!normalized) return '';
    const lines = normalized.split('\n');
    let index = 0;

    if (lines[index] === this.takeProfitCommentPrompt) {
      index += 1;
    }

    for (const question of this.visibleEntryQuestions()) {
      while (lines[index]?.startsWith(this.questionText(question.key))) {
        index += 1;
      }
    }

    if (lines[index] === '') {
      index += 1;
    }

    return lines.slice(index).join('\n').trim();
  }

  private visibleEntryQuestions(): { key: string }[] {
    return this.entryQuestions.filter((question) => this.isQuestionVisible(question.key));
  }

  isLongTermArea(): boolean {
    const label = this.activeJournalArea?.bezeichnung?.toLowerCase() ?? '';
    return label.includes('lang');
  }

  private isQuestionVisible(key: string): boolean {
    const isLongTerm = this.isLongTermArea();
    const lastTradesPositive = this.tradeForm.get('questionLastTradesPositive')?.value as boolean | null;

    const longTermOnly = new Set([
      'questionRsiOverbought',
      'questionRecommendedStock',
      'questionHighlySpeculative',
      'questionLowVolumeMoves',
    ]);
    if (longTermOnly.has(key)) {
      return isLongTerm;
    }

    const swingOnly = new Set([
      'questionMotivation',
      'questionRiskManagement',
      'questionLastTradesPositive',
      'questionReviewedLastTrades',
      'questionRsiUnder60',
      'questionTrendClear',
      'questionSignalCandle',
      'questionVolumeRising',
    ]);
    if (swingOnly.has(key) && isLongTerm) {
      return false;
    }

    if (key === 'questionReviewedLastTrades') {
      return lastTradesPositive === false;
    }

    return true;
  }

  private syncConditionalQuestions(): void {
    const lastTradesPositive = this.tradeForm.get('questionLastTradesPositive')?.value as boolean | null;
    const reviewedControl = this.tradeForm.get('questionReviewedLastTrades');
    const emotionalControl = this.tradeForm.get('questionEmotionalState');
    const optionalByVisibility = [
      'questionHigherTimeframe',
      'questionHigherTimeframeConfirmsTrend',
      'questionSupportResistance',
      'questionEma200',
      'questionEarningsSoon',
      'questionSetupConfidence',
      'questionMotivation',
      'questionRiskManagement',
      'questionLastTradesPositive',
      'questionRsiUnder60',
      'questionRsiOverbought',
      'questionTrendClear',
      'questionSignalCandle',
      'questionVolumeRising',
      'questionRecommendedStock',
      'questionHighlySpeculative',
      'questionLowVolumeMoves',
    ];

    optionalByVisibility.forEach((key) => {
      const control = this.tradeForm.get(key);
      if (!control) return;

      control.clearValidators();
      if (!this.isQuestionVisible(key)) {
        control.setValue(null, { emitEvent: false });
      }
      control.updateValueAndValidity({ emitEvent: false });
    });

    if (reviewedControl) {
      reviewedControl.clearValidators();
      if (!this.isQuestionVisible('questionReviewedLastTrades') || lastTradesPositive !== false) {
        reviewedControl.setValue(null, { emitEvent: false });
      }
      reviewedControl.updateValueAndValidity({ emitEvent: false });
    }

    if (emotionalControl) {
      emotionalControl.clearValidators();
      emotionalControl.updateValueAndValidity({ emitEvent: false });
    }
  }

  questionText(key: string): string {
    const direction = this.tradeForm.get('direction')?.value as TradeDirection | null;
    const config = this.questionTextMap[key];
    if (!config) return '';
    if (direction === 'Long' && config.long) return config.long;
    if (direction === 'Short' && config.short) return config.short;
    return config.text ?? config.long ?? config.short ?? '';
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  riskPerShare(): number {
    const entry = Number(this.tradeForm.value.entryPrice ?? 0);
    const stop = Number(this.tradeForm.value.stopPrice ?? 0);
    const direction = this.calculationDirectionForForm();
    const diff = direction === 'Long' ? entry - stop : stop - entry;
    if (diff <= 0) {
      return 0;
    }

    if (this.isCfdSelected()) {
      const marge = this.validPositiveNumber(this.tradeForm.value.marge);
      return marge ? +(diff * (marge / 100)).toFixed(4) : diff;
    }

    return diff;
  }

  private async loadAppSettings(): Promise<void> {
    try {
      const settings = await firstValueFrom(this.api.getAppSettings());
      this.showAiAndJsonInputs = settings.aiJsonInputsEnabled === true;
    } catch {
      this.showAiAndJsonInputs = false;
    }
  }

  potentialReward(): number {
    const entry = Number(this.tradeForm.value.entryPrice ?? 0);
    const target = Number(this.tradeForm.value.targetPrice ?? 0);
    const size = Number(this.tradeForm.value.size ?? 0);
    const direction = this.calculationDirectionForForm();
    const delta = direction === 'Long' ? target - entry : entry - target;
    return delta > 0 ? delta * size * this.formPriceMultiplier() : 0;
  }

  potentialLoss(): number {
    const risk = this.riskPerShare();
    const size = Number(this.tradeForm.value.size ?? 0);
    return risk * size;
  }

  maxPositionSize(): number {
    const risk = this.riskPerShare();
    const budget = this.riskPerTradeEuro;
    const baseSize = risk > 0 ? Math.floor(budget / risk) : 0;
    return this.adjustedPositionSize(baseSize);
  }

  potentialProfit(): number {
    const reward = this.potentialReward();
    return reward > 0 ? reward : 0;
  }

  riskRewardPreview(): number {
    const entry = Number(this.tradeForm.value.entryPrice ?? 0);
    const stop = Number(this.tradeForm.value.stopPrice ?? 0);
    const target = Number(this.tradeForm.value.targetPrice ?? 0);
    const direction = this.calculationDirectionForForm();

    const risk = direction === 'Long'
      ? entry - stop
      : stop - entry;

    const reward = direction === 'Long'
      ? target - entry
      : entry - target;

    if (risk <= 0 || reward <= 0) {
      return 0;
    }

    return +(reward / risk).toFixed(2);
  }

  targetPricePreview(): number {
    const entry = Number(this.tradeForm.value.entryPrice ?? 0);
    const stop = Number(this.tradeForm.value.stopPrice ?? 0);
    const ratio = this.selectedCrvRatio();
    const direction = this.calculationDirectionForForm();

    if (entry <= 0 || stop <= 0 || ratio <= 0) {
      return 0;
    }

    const risk = Math.abs(entry - stop);
    if (risk <= 0) {
      return 0;
    }

    const target = direction === 'Long'
      ? entry + risk * ratio
      : entry - risk * ratio;

    return +target.toFixed(2);
  }

  private calculationDirectionForForm(): TradeDirection {
    return this.isCertificateType(this.tradeForm.value.tradeType) || this.tradeForm.value.certificate
      ? 'Long'
      : this.tradeForm.value.direction as TradeDirection;
  }

  private formPriceMultiplier(): number {
    if (!this.isCfdSelected()) return 1;
    const marge = this.validPositiveNumber(this.tradeForm.value.marge);
    return marge ? marge / 100 : 1;
  }

  private priceMultiplier(entry: TradeEntry): number {
    if (!this.isCfdEntry(entry)) return 1;
    const marge = this.validPositiveNumber(entry.marge);
    return marge ? marge / 100 : 1;
  }

  private isCfdEntry(entry: TradeEntry): boolean {
    return entry.tradeType === 'CFD';
  }

  private cfdCostPerUnit(entry: TradeEntry): number {
    if (!this.isCfdEntry(entry)) return 0;
    const cfd = this.cfdDetails.find((item) => item.bezeichnung === entry.symbol);
    return this.validPositiveNumber(cfd?.kostenProEinheit) ?? 0;
  }

  private synchronizeCfdEntryFee(): void {
    if (!this.isCfdSelected() || this.cfdEntryFeeManuallyEdited) return;
    const costPerUnit = this.validPositiveNumber(this.selectedCfdDetail()?.kostenProEinheit) ?? 0;
    const size = Number(this.tradeForm.value.size ?? 0);
    const entryFee = +(costPerUnit * Math.max(size, 0) / 2).toFixed(2);
    this.tradeForm.get('entryFee')?.setValue(entryFee, { emitEvent: false });
  }

  private calculationDirection(entry: TradeEntry): TradeDirection {
    const entryPrice = Number(entry.entryPrice);
    const stop = Number(entry.initialStopPrice ?? entry.stopPricePrevious ?? entry.stopPrice);
    const target = Number(entry.targetPrice);

    if (Number.isFinite(entryPrice) && Number.isFinite(stop) && Number.isFinite(target)) {
      if (stop < entryPrice && target > entryPrice) return 'Long';
      if (stop > entryPrice && target < entryPrice) return 'Short';
    }

    return this.isCertificateType(entry.tradeType) || entry.certificate ? 'Long' : entry.direction;
  }

  totalFees(entry: TradeEntry): number {
    const partialFees = entry.partialSales?.reduce((acc, sale) => acc + (sale.fee ?? 0), 0) ?? 0;
    return +(partialFees + (entry.sellFee ?? 0)).toFixed(2);
  }

  realizedPnl(entry: TradeEntry): number {
    if (entry.partialSales?.length) {
      return entry.partialSales.reduce((sum, sale) => sum + sale.pnl, 0);
    }

    const sellPrice = entry.sellPrice;
    const size = entry.sellSize ?? entry.size;
    const entryFee = entry.entryFee ?? 0;
    const sellFee = entry.sellFee ?? 0;

    if (sellPrice === undefined || size <= 0) {
      return entry.realizedPnl ?? 0;
    }

    const perShare = this.calculationDirection(entry) === 'Long'
      ? sellPrice - entry.entryPrice
      : entry.entryPrice - sellPrice;

    const total = perShare * size * this.priceMultiplier(entry) - entryFee - sellFee;
    return +total.toFixed(2);
  }

  profitClass(entry: TradeEntry): string {
    const pnl = this.realizedPnl(entry);
    if (this.normalizeStatus(entry.status, 'Watch') !== 'Glatt' || pnl === 0) return '';
    return pnl > 0 ? 'row-profit' : 'row-loss';
  }

  activePositionClass(entry: TradeEntry): string {
    return this.isOpenPosition(entry) ? 'row-active-position' : '';
  }

  private isOpenPosition(entry: TradeEntry): boolean {
    const status = this.normalizeStatus(entry.status, entry.positioned ? 'Positioniert' : 'Watch');
    if (status === 'Glatt' || status === 'Storniert' || status === 'Watch') {
      return false;
    }
    return entry.positioned !== false || status === 'Positioniert' || status === 'Teilverkauf';
  }

  private normalizeStatus(status: string | undefined, fallback: TradeState): TradeState {
    const value = (status ?? '').toString().trim().toLowerCase();
    if (value === 'w' || value === 'watch') return 'Watch';
    if (value === 'p' || value === 'positioniert') return 'Positioniert';
    if (value === 't' || value === 'teilverkauf') return 'Teilverkauf';
    if (value === 'g' || value === 'glatt') return 'Glatt';
    if (value === 's' || value === 'storniert') return 'Storniert';
    return fallback;
  }

  shortStatus(status: string): string {
    return status ? status.charAt(0) : '';
  }

  shortDirection(direction: string): string {
    if (direction.toLowerCase() === 'long') return 'L';
    if (direction.toLowerCase() === 'short') return 'S';
    return direction;
  }

  shortSetup(setup: string): string {
    return setup ? setup.charAt(0) : '';
  }

  currencySymbol(currency?: TradeCurrency): string {
    return currency === 'USD' ? '$' : '€';
  }

  plannedEntries(): TradeEntry[] {
    const entries = this.entries().filter((entry) => entry.status === 'Watch' || entry.status === 'Storniert');
    if (this.watchStatusFilter === 'open') {
      return entries.filter((entry) => entry.status === 'Watch');
    }
    if (this.watchStatusFilter === 'storno') {
      return entries.filter((entry) => entry.status === 'Storniert');
    }
    return entries;
  }

  plannedVolume(): number {
    const volume = this.plannedEntries().reduce((sum, entry) => sum + this.positionVolume(entry), 0);
    return +volume.toFixed(2);
  }

  positionedEntries(): TradeEntry[] {
    return this.entries().filter((entry) => entry.status !== 'Watch' && entry.status !== 'Storniert');
  }

  openPositionVolume(): number {
    const volume = this.positionedEntries()
      .filter((entry) => this.isOpenPosition(entry))
      .reduce((sum, entry) => sum + this.positionVolume(entry), 0);
    return +volume.toFixed(2);
  }

  positionedRealizedSum(): number {
    const total = this.filteredPositionedEntries().reduce((sum, entry) => sum + this.displayPnl(entry), 0);
    return +total.toFixed(2);
  }

  positionedRealizedRSum(): number {
    const total = this.filteredPositionedEntries()
      .reduce((sum, entry) => sum + this.realizedResultR(entry), 0);
    return +total.toFixed(2);
  }

  maxRiskOpen(): number {
    const total = this.filteredPositionedEntries()
      .filter((entry) => this.isOpenPosition(entry))
      .reduce((sum, entry) => sum + this.initialRiskTotal(entry), 0);
    return +total.toFixed(2);
  }

  displayedPositionedEntries(): TradeEntry[] {
    const sorted = this.sortedPositionedEntries();
    const start = (this.currentPage - 1) * this.pageSize;
    return sorted.slice(start, start + this.pageSize);
  }

  displayedPlannedEntries(): TradeEntry[] {
    const sorted = this.sortedPlannedEntries();
    const start = (this.watchCurrentPage - 1) * this.watchPageSize;
    return sorted.slice(start, start + this.watchPageSize);
  }

  totalPlannedPages(): number {
    const total = this.plannedEntries().length;
    if (!total) return 1;
    return Math.max(1, Math.ceil(total / this.watchPageSize));
  }

  totalPositionedPages(): number {
    const total = this.filteredPositionedEntries().length;
    if (!total) return 1;
    return Math.max(1, Math.ceil(total / this.pageSize));
  }

  private today(): string {
    return new Date().toISOString().substring(0, 10);
  }

  private positionVolume(entry: TradeEntry): number {
    return (entry.entryPrice ?? 0) * (entry.size ?? 0);
  }

  private riskPerEntry(entry: TradeEntry): number {
    const stop = entry.stopPrice;
    const entryPrice = entry.entryPrice;
    if (stop === undefined || entryPrice === undefined) return 0;
    const diff = Math.abs(entryPrice - stop);
    return diff * (entry.size ?? 0);
  }

  pnlClass(pnl: number): string {
    if (pnl > 0) return 'pnl-positive';
    if (pnl < 0) return 'pnl-negative';
    return '';
  }

  displayPnl(entry: TradeEntry): number {
    return this.convertToEuro(this.pnlInTradeCurrency(entry), entry.currency);
  }

  private pnlInTradeCurrency(entry: TradeEntry): number {
    if (entry.partialSales?.length) {
      const total = entry.partialSales.reduce((sum, sale) => {
        const perShare = this.calculationDirection(entry) === 'Long'
          ? sale.price - entry.entryPrice
          : entry.entryPrice - sale.price;
        return sum + perShare * sale.size;
      }, 0);
      return +total.toFixed(2);
    }

    const sellPrice = entry.sellPrice;
    const soldSize = entry.sellSize ?? entry.size;
    if (sellPrice === undefined || soldSize <= 0) {
      return this.realizedPnl(entry);
    }

    const perShare = this.calculationDirection(entry) === 'Long'
      ? sellPrice - entry.entryPrice
      : entry.entryPrice - sellPrice;

    return +(perShare * soldSize).toFixed(2);
  }

  displayPartialPnl(entry: TradeEntry, pnl: number): number {
    return this.convertToEuro(pnl, entry.currency);
  }

  displayPartialPnlSum(entry: TradeEntry): number {
    return this.convertToEuro(this.partialPnlSum(entry), entry.currency);
  }

  stopDisplayParts(entry: TradeEntry): { previous?: number; current: number } {
    const current = entry.stopPrice;
    const previous = entry.stopPricePrevious;
    return previous !== undefined && previous !== current
      ? { previous, current }
      : { current };
  }

  private appendStopComment(comment: string, previousStop: number, newStop: number, reason = ''): string {
    const trimmed = (comment ?? '').toString().trim();
    if (previousStop === newStop) return trimmed;
    const note = `Stop geändert: ${previousStop} → ${newStop}. Begründung: ${reason.trim()}`;
    return trimmed ? `${trimmed} | ${note}` : note;
  }

  takeProfitDisplayParts(entry: TradeEntry): { original: number; actual?: number } {
    const actual = this.averageSellPrice(entry) ?? entry.sellPrice;
    return actual !== undefined && actual > 0
      ? { original: entry.targetPrice, actual }
      : { original: entry.targetPrice };
  }

  openPositionDialog(entry: TradeEntry): void {
    this.closeSellDialog();
    this.sellDialogEntryId = entry.id;
    this.sellDialogMode = 'position';
    this.positionForm.setValue({
      entryPrice: entry.entryPrice,
      entryDate: entry.entryDate || this.today(),
      comment: entry.comment ?? ''
    });
  }

  async confirmPositionDialog(): Promise<void> {
    if (!this.sellDialogEntryId || this.positionForm.invalid) return;
    const entry = this.entries().find((e) => e.id === this.sellDialogEntryId);
    if (!entry) return;

    const { entryPrice, entryDate, comment } = this.positionForm.value;
    const changed: TradeEntry = {
      ...entry,
      positioned: true,
      status: 'Positioniert' as TradeState,
      entryDate: entryDate || this.today(),
      entryPrice: Number(entryPrice),
      comment: comment?.toString().trim() || undefined,
    };

    try {
      const saved = await this.saveEntry(changed);
      const updated = this.entries().map((e) => (e.id === changed.id ? saved : e));
      this.entries.set(updated);
      this.updatePositionedPnl();
      this.ensurePageWithinBounds();
      this.closePositionDialog();
    } catch {
      // Fehler bereits angezeigt
    }
  }

  closePositionDialog(): void {
    this.sellDialogEntryId = null;
    this.sellDialogMode = null;
    this.positionForm.reset({ entryPrice: 0, entryDate: this.today(), comment: '' });
  }

  openStornoDialog(entry: TradeEntry): void {
    this.closeSellDialog();
    this.closePositionDialog();
    this.stornoDialogEntryId = entry.id;
    this.stornoScreenshotUrl = '';
    this.stornoScreenshotUploadName = '';
    this.stornoScreenshotUploadError = '';
    this.stornoScreenshotUploadDataUrl = '';
    this.stornoScreenshotDropActive = false;
  }

  closeStornoDialog(): void {
    this.stornoDialogEntryId = null;
    this.stornoScreenshotUrl = '';
    this.stornoScreenshotUploadName = '';
    this.stornoScreenshotUploadError = '';
    this.stornoScreenshotUploadDataUrl = '';
    this.stornoScreenshotDropActive = false;
  }

  setFlat(id: string): void {
    const entry = this.entries().find((item) => item.id === id);
    if (!entry) return;

    this.closePositionDialog();
    this.sellDialogEntryId = id;
    this.sellDialogMode = 'sell';
    const remaining = this.remainingSize(entry);
    this.sellForm.setValue({
      sellPrice: entry.sellPrice ?? entry.targetPrice ?? entry.entryPrice,
      sellSize: remaining,
      sellDate: this.today(),
      sellScreenshotUrl: '',
      sellFee: this.isCfdEntry(entry)
        ? +(this.cfdCostPerUnit(entry) * remaining / 2).toFixed(2)
        : entry.sellFee ?? 0,
      sellComment: entry.comment,
      rulesFollowed: null,
      planFollowed: entry.planFollowed ?? null,
      entryWasValid: entry.entryWasValid ?? null,
      exitWasValid: entry.exitWasValid ?? null,
      stopWasRespected: entry.stopWasRespected ?? null,
      positionSizeWasCorrect: entry.positionSizeWasCorrect ?? null,
      ruleBroken: entry.ruleBroken ?? null
    });
  }

  onRulesFollowedChange(value: boolean): void {
    this.setBinaryChoice(this.sellForm, 'rulesFollowed', value);
    this.syncRulesFollowedComment();
  }

  onDailyCheckChange(controlName: string, value: boolean): void {
    this.setBinaryChoice(this.dailyCheckForm, controlName, value);
    this.syncDailyCheckValidators();
    this.syncDailyCheckComment();
  }

  private currentDailyCheckEntry(): TradeEntry | undefined {
    return this.dailyCheckEntryId
      ? this.entries().find((item) => item.id === this.dailyCheckEntryId)
      : undefined;
  }

  isDailyCheckLongTerm(): boolean {
    const entry = this.currentDailyCheckEntry();
    if (!entry) return this.isLongTermArea();
    return this.isLongTermByFeldTextId(entry.feldTextId);
  }

  private isLongTermByFeldTextId(feldTextId?: string): boolean {
    if (!feldTextId) return this.isLongTermArea();
    const area = this.journalAreas.find((item) => item.feldTextId === feldTextId);
    const label = area?.bezeichnung?.toLowerCase() ?? '';
    return label.includes('lang');
  }

  private syncDailyCheckValidators(): void {
    const structureControl = this.dailyCheckForm.get('structureIntact');
    const assumptionControl = this.dailyCheckForm.get('assumptionViolated');
    const stopControl = this.dailyCheckForm.get('stopCorrect');
    const earningsControl = this.dailyCheckForm.get('earningsSoon');
    const rulesControl = this.dailyCheckForm.get('rulesFollowed');
    const ltTrendControl = this.dailyCheckForm.get('longTermTrendIntact');
    const ltRsiControl = this.dailyCheckForm.get('longTermRsiExtreme');
    const ltStopControl = this.dailyCheckForm.get('longTermStopCorrect');
    if (!structureControl || !assumptionControl || !stopControl || !earningsControl || !rulesControl || !ltTrendControl || !ltRsiControl || !ltStopControl) return;

    structureControl.setValidators(Validators.required);
    assumptionControl.setValidators(Validators.required);
    stopControl.setValidators(Validators.required);
    earningsControl.setValidators(Validators.required);
    rulesControl.setValidators(Validators.required);

    if (this.isDailyCheckLongTerm()) {
      ltTrendControl.setValidators(Validators.required);
      ltRsiControl.setValidators(Validators.required);
      const rsiExtreme = ltRsiControl.value as boolean | null;
      if (rsiExtreme === true) {
        ltStopControl.setValidators(Validators.required);
      } else {
        ltStopControl.clearValidators();
        ltStopControl.setValue(null, { emitEvent: false });
      }
    } else {
      ltTrendControl.clearValidators();
      ltRsiControl.clearValidators();
      ltStopControl.clearValidators();
      ltTrendControl.setValue(null, { emitEvent: false });
      ltRsiControl.setValue(null, { emitEvent: false });
      ltStopControl.setValue(null, { emitEvent: false });
    }

    structureControl.updateValueAndValidity({ emitEvent: false });
    assumptionControl.updateValueAndValidity({ emitEvent: false });
    stopControl.updateValueAndValidity({ emitEvent: false });
    earningsControl.updateValueAndValidity({ emitEvent: false });
    rulesControl.updateValueAndValidity({ emitEvent: false });
    ltTrendControl.updateValueAndValidity({ emitEvent: false });
    ltRsiControl.updateValueAndValidity({ emitEvent: false });
    ltStopControl.updateValueAndValidity({ emitEvent: false });
  }

  openDailyCheck(entry: TradeEntry): void {
    this.dailyCheckEntryId = entry.id;
    this.dailyCheckBaseComment = entry.comment?.toString().trim() || '';
    this.dailyCheckForm.reset({
      structureIntact: null,
      assumptionViolated: null,
      stopCorrect: null,
      earningsSoon: null,
      rulesFollowed: null,
      longTermTrendIntact: null,
      longTermRsiExtreme: null,
      longTermStopCorrect: null,
      comment: this.dailyCheckBaseComment,
    });
    this.syncDailyCheckValidators();
  }

  onDailyCheckCommentInput(): void {
    const commentControl = this.dailyCheckForm.get('comment');
    if (!commentControl) return;
    this.dailyCheckBaseComment = commentControl.value?.toString() ?? '';
    this.syncDailyCheckComment();
  }

  submitDailyCheck(): void {
    this.syncDailyCheckValidators();
    if (this.dailyCheckForm.invalid) return;
    const entry = this.dailyCheckEntryId
      ? this.entries().find((item) => item.id === this.dailyCheckEntryId)
      : undefined;
    if (!entry) {
      this.closeDailyCheck();
      return;
    }

    const { structureIntact, assumptionViolated, stopCorrect, earningsSoon, rulesFollowed, longTermTrendIntact, longTermRsiExtreme, longTermStopCorrect } = this.dailyCheckForm.value;
    const block = this.buildDailyCheckBlock(
      structureIntact,
      assumptionViolated,
      stopCorrect,
      earningsSoon,
      rulesFollowed,
      this.isDailyCheckLongTerm(),
      longTermTrendIntact,
      longTermRsiExtreme,
      longTermStopCorrect,
    );
    const base = this.stripTodayDailyCheckBlock(this.dailyCheckBaseComment).trimEnd();
    const combined = base ? `${base}\n\n${block}` : block;
    const changed: TradeEntry = { ...entry, comment: combined };
    this.saveEntry(changed)
      .then((saved) => {
        const updated = this.entries().map((current) => (current.id === saved.id ? saved : current));
        this.entries.set(updated);
      })
      .finally(() => this.closeDailyCheck());
  }

  closeDailyCheck(): void {
    this.dailyCheckEntryId = null;
    this.dailyCheckBaseComment = '';
    this.dailyCheckForm.reset({
      structureIntact: null,
      assumptionViolated: null,
      stopCorrect: null,
      earningsSoon: null,
      rulesFollowed: null,
      longTermTrendIntact: null,
      longTermRsiExtreme: null,
      longTermStopCorrect: null,
      comment: '',
    });
    this.syncDailyCheckValidators();
  }

  isSaveDisabled(): boolean {
    return this.tradeForm.invalid || !this.hasScreenshotInput();
  }

  onAnalysisDragOver(event: DragEvent): void {
    event.preventDefault();
    this.analysisDropActive = true;
  }

  onAnalysisDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.analysisDropActive = false;
  }

  handleAnalysisDrop(event: DragEvent): void {
    event.preventDefault();
    this.analysisDropActive = false;
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this.processAnalysisFile(file);
    }
  }

  handleAnalysisFileSelection(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.processAnalysisFile(file);
    }
    input.value = '';
  }

  onAnalysisImageDragOver(event: DragEvent): void {
    event.preventDefault();
    this.analysisImageDropActive = true;
  }

  onAnalysisImageDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.analysisImageDropActive = false;
  }

  handleAnalysisImageDrop(event: DragEvent): void {
    event.preventDefault();
    this.analysisImageDropActive = false;
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this.processAnalysisImageFile(file);
    }
  }

  handleAnalysisImageFileSelection(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.processAnalysisImageFile(file);
    }
    input.value = '';
  }

  handleAnalysisImagePaste(event: ClipboardEvent): void {
    const file = this.imageFileFromClipboard(event);
    if (file) {
      event.preventDefault();
      this.processAnalysisImageFile(file);
    }
  }

  openAnalysisDialog(): void {
    if (this.hasAnalysisVariants(this.analysisData)) {
      this.analysisDialogOpen = true;
    }
  }

  closeAnalysisDialog(): void {
    this.analysisDialogOpen = false;
  }

  applyAnalysisVariant(variant: AnalysisVariant): void {
    const variants = this.analysisData?.trade_variants?.length
      ? this.analysisData.trade_variants
      : this.analysisData?.varianten?.length
        ? this.analysisData.varianten
        : this.analysisData?.szenarien ?? [];
    this.selectedAnalysisVariant = variant;
    this.selectedAnalysisVariantIndex = Math.max(0, variants.indexOf(variant));

    const setup = this.analysisSetup(variant);
    if (!this.setups.includes(setup)) {
      this.setups = [...this.setups, setup];
    }

    const symbol = this.analysisData?.symbol
      || this.analysisData?.instrument
      || this.analysisData?.extrahierteDaten?.['symbol']?.toString()
      || this.tradeForm.value.symbol;
    const currency = this.currencyFromInstrument(symbol);
    const patch: Record<string, unknown> = {
      symbol,
      entryDate: '',
      direction: this.analysisDirection(variant),
      setup,
      currency,
    };
    if (this.hasAnalysisTradePlan(variant)) {
      patch['entryPrice'] = Number(variant.entry ?? variant.einstieg ?? 0);
      patch['stopPrice'] = Number(variant.stop_loss ?? variant.stopLos ?? variant.invalidierung ?? 0);
      patch['targetPrice'] = Number(variant.take_profit ?? variant.ziel ?? 0);
    }
    this.tradeForm.patchValue(patch);

    this.manualComment = this.buildAnalysisComment(variant);
    this.commentSyncFn?.();
    this.loadExchangeRate(currency);
    this.recalculatePositionAndProfit('init');
    if (this.hasAnalysisTradePlan(variant)) {
      const schemaQuantity = Number(variant.position_size?.quantity ?? 0);
      this.tradeForm.get('size')?.setValue(schemaQuantity > 0 ? schemaQuantity : this.maxPositionSize(), { emitEvent: false });
    }
    this.closeAnalysisDialog();
  }

  onScreenshotDragOver(event: DragEvent): void {
    event.preventDefault();
    this.screenshotDropActive = true;
  }

  onScreenshotDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.screenshotDropActive = false;
  }

  handleScreenshotDrop(event: DragEvent): void {
    event.preventDefault();
    this.screenshotDropActive = false;
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this.processScreenshotFile(file);
    }
  }

  handleScreenshotFileSelection(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.processScreenshotFile(file);
    }
    input.value = '';
  }

  handleSizeBlur(): void {
    this.recalculatePositionAndProfit('size');
    this.checkPositionSize();
  }

  closeWarning(): void {
    this.warningMessage = '';
  }

  handleScreenshotPaste(event: ClipboardEvent): void {
    const file = this.imageFileFromClipboard(event);
    if (file) {
      event.preventDefault();
      this.processScreenshotFile(file);
    }
  }


  clearScreenshotUploadMetadata(): void {
    this.screenshotUploadName = '';
    this.screenshotUploadError = '';
    this.screenshotUploadDataUrl = '';
  }

  onStornoScreenshotDragOver(event: DragEvent): void {
    event.preventDefault();
    this.stornoScreenshotDropActive = true;
  }

  onStornoScreenshotDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.stornoScreenshotDropActive = false;
  }

  handleStornoScreenshotDrop(event: DragEvent): void {
    event.preventDefault();
    this.stornoScreenshotDropActive = false;
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this.processStornoScreenshotFile(file);
    }
  }

  handleStornoScreenshotFileSelection(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.processStornoScreenshotFile(file);
      input.value = '';
    }
  }

  handleStornoScreenshotPaste(event: ClipboardEvent): void {
    const file = this.imageFileFromClipboard(event);
    if (file) {
      event.preventDefault();
      this.processStornoScreenshotFile(file);
    }
  }

  clearStornoScreenshotUploadMetadata(): void {
    this.stornoScreenshotUploadName = '';
    this.stornoScreenshotUploadError = '';
    this.stornoScreenshotUploadDataUrl = '';
  }

  private processAnalysisFile(file: File): void {
    this.analysisUploadError = '';
    if (!this.isJsonFile(file)) {
      this.analysisUploadError = 'Nur JSON-Dateien sind erlaubt.';
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const rawJson = reader.result?.toString() ?? '';
        const normalizedJson = this.loadAnalysisFromJson(rawJson, file.name || 'analyse.json');
        await this.persistAnalysisFile(file.name || 'analyse.json', normalizedJson);
      } catch {
        this.analysisUploadError = 'Die JSON konnte nicht gelesen werden.';
        this.analysisData = null;
        this.analysisFileName = '';
        this.analysisPersistedId = '';
        this.analysisRawJson = '';
      }
    };
    reader.onerror = () => {
      this.analysisUploadError = 'Die JSON konnte nicht geladen werden.';
    };
    reader.readAsText(file);
  }

  private imageFileFromClipboard(event: ClipboardEvent): File | null {
    const items = event.clipboardData?.items;
    if (!items?.length) return null;
    const imageItem = Array.from(items).find((item) => item.type.startsWith('image/'));
    return imageItem?.getAsFile() ?? null;
  }

  private processAnalysisImageFile(file: File): void {
    this.analysisUploadError = '';
    if (!this.isImageFile(file)) {
      this.analysisUploadError = 'Nur Bilddateien (z. B. PNG oder JPG) sind erlaubt.';
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      this.analysisImageRunning = true;
      this.analysisImageUploadName = file.name || 'chart.png';
      try {
        const imageDataUrl = reader.result?.toString() ?? '';
        const response = await firstValueFrom(this.api.runChartAnalysis({
          fileName: file.name || 'chart.png',
          imageDataUrl,
        }));

        if (!response?.success || !response.rawJson) {
          this.analysisUploadError = response?.message || 'Chartanalyse konnte nicht erstellt werden.';
          return;
        }

        this.loadAnalysisFromJson(
          response.rawJson,
          response.item?.fileName || `${file.name || 'chart.png'}.openai.json`,
          response.item?.id ?? ''
        );
      } catch (error) {
        this.analysisUploadError = this.formatError(error) || 'Chartanalyse konnte nicht erstellt werden.';
      } finally {
        this.analysisImageRunning = false;
      }
    };
    reader.onerror = () => {
      this.analysisUploadError = 'Das Bild konnte nicht geladen werden.';
      this.analysisImageRunning = false;
    };
    reader.readAsDataURL(file);
  }

  private loadAnalysisFromJson(rawJson: string, fileName: string, persistedId = ''): string {
    const parsed = this.normalizeAnalysisFile(JSON.parse(rawJson) as AnalysisFile);
    if (!this.isValidAnalysisFile(parsed)) {
      throw new Error('Die Analyse enthaelt keine verwertbaren Varianten.');
    }

    const normalizedJson = JSON.stringify(parsed);
    this.analysisData = parsed;
    this.analysisFileName = fileName;
    this.analysisRawJson = normalizedJson;
    this.analysisPersistedId = persistedId;
    this.analysisUploadError = '';
    this.analysisDialogOpen = true;
    return normalizedJson;
  }

  private async persistAnalysisFile(fileName: string, rawJson: string): Promise<void> {
    try {
      const response = await firstValueFrom(this.api.createTradeAnalysis({ fileName, rawJson }));
      if (response?.success && response.item?.id) {
        this.analysisPersistedId = response.item.id;
        return;
      }
      this.analysisPersistedId = '';
      this.analysisUploadError = response?.message || 'Analyse konnte nicht gespeichert werden.';
    } catch (error) {
      this.analysisPersistedId = '';
      this.analysisUploadError = this.formatError(error) || 'Analyse konnte nicht gespeichert werden.';
    }
  }

  private isValidAnalysisFile(value: AnalysisFile): boolean {
    return (Array.isArray(value?.trade_variants) && value.trade_variants.length > 0)
      || (Array.isArray(value?.varianten) && value.varianten.length > 0)
      || (Array.isArray(value?.szenarien) && value.szenarien.length > 0);
  }

  private normalizeAnalysisFile(value: AnalysisFile): AnalysisFile {
    const normalized: AnalysisFile = { ...value };
    if (!normalized.instrument && normalized.symbol) {
      normalized.instrument = normalized.symbol;
    }
    if (!normalized.instrument && normalized.extrahierteDaten?.['symbol']) {
      normalized.instrument = normalized.extrahierteDaten['symbol']?.toString();
    }
    if (!normalized.datumanalyse && normalized.analyseDatum) {
      normalized.datumanalyse = normalized.analyseDatum;
    }
    if ((!normalized.varianten || !normalized.varianten.length) && normalized.szenarien?.length) {
      normalized.varianten = normalized.szenarien.map((scenario) => this.analysisScenarioToVariant(scenario));
    }
    return normalized;
  }

  hasAnalysisVariants(value: AnalysisFile | null): boolean {
    return !!value
      && ((Array.isArray(value.trade_variants) && value.trade_variants.length > 0)
        || (Array.isArray(value.varianten) && value.varianten.length > 0)
        || (Array.isArray(value.szenarien) && value.szenarien.length > 0));
  }

  private hasAnalysisTradePlan(variant: AnalysisVariant): boolean {
    const hasSchemaPlan = variant.entry != null && variant.stop_loss != null && variant.take_profit != null;
    const hasLegacyPlan = variant.einstieg != null && (variant.stopLos != null || variant.invalidierung != null) && variant.ziel != null;
    return hasSchemaPlan || hasLegacyPlan;
  }

  private analysisScenarioToVariant(scenario: AnalysisVariant): AnalysisVariant {
    const direction = (scenario.richtung ?? scenario.trend ?? '').toLowerCase();
    const trend = direction === 'short' ? 'short' : 'long';
    const szenario = direction === 'neutral'
      ? 'Seitwert'
      : scenario.szenario?.toLowerCase().includes('korrektur')
        ? 'Trendfolge'
        : scenario.szenario?.toLowerCase().includes('konsolidierung')
          ? 'Seitwert'
          : scenario.szenario?.toLowerCase().includes('erholung')
            ? 'Seitwert'
            : scenario.szenario ?? 'Ausbruch';

    return {
      ...scenario,
      name: scenario.name ?? scenario.szenario,
      szenario,
      trend,
      setup: trend,
      einstieg: this.firstScenarioNumber(scenario.einstieg, scenario.einstiegZone, scenario.bereich, scenario.ziel),
      stopLos: this.firstScenarioNumber(scenario.stopLos, scenario.stopLoss, scenario.invalidierung),
      ziel: this.firstScenarioNumber(scenario.ziel, scenario.ziel1, scenario.erweitertesZiel),
      invalidierung: scenario.invalidierung ?? scenario.stopLos ?? scenario.stopLoss,
    };
  }

  private firstScenarioNumber(...values: unknown[]): number | undefined {
    for (const value of values) {
      if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
      }
      if (typeof value === 'string') {
        const match = value.match(/-?\d+(?:[.,]\d+)?/);
        if (match) {
          return Number(match[0].replace(',', '.'));
        }
      }
    }
    return undefined;
  }

  private isJsonFile(file: File): boolean {
    return file.type === 'application/json' || file.name?.toLowerCase().endsWith('.json');
  }

  private hasNumber(value: unknown): boolean {
    return typeof value === 'number' && Number.isFinite(value);
  }

  analysisDirection(variant: AnalysisVariant): TradeDirection {
    return variant.direction?.toLowerCase() === 'short'
      || variant.trend?.toLowerCase() === 'short'
      || variant.setup?.toLowerCase() === 'short'
      ? 'Short'
      : 'Long';
  }

  analysisCandidateDirection(candidate: AnalysisScenarioCandidate): TradeDirection {
    return candidate.trend?.toLowerCase() === 'short' ? 'Short' : 'Long';
  }

  analysisScoreEntries(scoreDetails?: Record<string, number>): Array<{ label: string; value: number }> {
    return Object.entries(scoreDetails ?? {}).map(([key, value]) => ({
      label: this.analysisLabel(key),
      value,
    }));
  }

  analysisValidationEntries(): Array<{ label: string; value: boolean | number | string }> {
    return Object.entries(this.analysisData?.validierung ?? {}).map(([key, value]) => ({
      label: this.analysisLabel(key),
      value: this.analysisValue(value),
    }));
  }

  analysisChecklistEntries(): Array<{ label: string; value: string }> {
    return this.analysisObjectEntries(this.analysisData?.checkliste);
  }

  analysisEmaEntries(): Array<{ label: string; value: string }> {
    return this.analysisObjectEntries(this.analysisData?.emaAnalyse);
  }

  analysisEmaRatingEntries(variant: AnalysisVariant): Array<{ label: string; value: string }> {
    return this.analysisObjectEntries(variant.emaBewertung);
  }

  analysisExactConfirmationEntries(variant: AnalysisVariant): Array<{ label: string; value: string }> {
    return this.analysisObjectEntries(variant.bestaetigungExakt);
  }

  analysisDataGapText(): string {
    const value = this.analysisData?.datenluecken;
    if (!value) return '';
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'object') {
      return Object.entries(value)
        .map(([key, item]) => `${this.analysisLabel(key)}: ${item}`)
        .join(', ');
    }
    return value;
  }

  private analysisObjectEntries(value?: Record<string, unknown>): Array<{ label: string; value: string }> {
    return Object.entries(value ?? {}).map(([key, item]) => ({
      label: this.analysisLabel(key),
      value: this.analysisValue(item),
    }));
  }

  private analysisValue(value: unknown): string {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value ? 'Ja' : 'Nein';
    if (typeof value === 'number') return Number.isFinite(value) ? value.toString() : '-';
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) return value.map((item) => this.analysisValue(item)).join(', ');
    if (typeof value === 'object') {
      return Object.entries(value as Record<string, unknown>)
        .map(([key, item]) => `${this.analysisLabel(key)}: ${this.analysisValue(item)}`)
        .join(' | ');
    }
    return String(value);
  }

  private analysisLabel(key: string): string {
    return key
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/_/g, ' ')
      .replace(/^./, (char) => char.toUpperCase());
  }

  private analysisSetup(variant: AnalysisVariant): string {
    const raw = (variant.setup_type ?? variant.szenario ?? '').trim();
    const normalized = raw.toLowerCase() === 'seitwerts' ? 'Seitwert' : raw;
    const known = this.setups.find((setup) => setup.toLowerCase() === normalized.toLowerCase());
    return known ?? (normalized || 'Ausbruch');
  }

  private variantName(variant: AnalysisVariant): string {
    return variant.variant_name || variant.name || variant.szenario || `Variante ${(this.selectedAnalysisVariantIndex ?? 0) + 1}`;
  }

  private currencyFromInstrument(instrument?: string): TradeCurrency {
    const value = instrument?.toUpperCase() ?? '';
    return value.includes('USD') ? 'USD' : 'EUR';
  }

  private buildAnalysisComment(variant: AnalysisVariant): string {
    if (this.analysisData?.trade_variants?.length) {
      return this.buildSchemaAnalysisComment(variant);
    }
    const triggers = (variant.trigger ?? [])
      .map((trigger, index) => `${index + 1}. Wenn ${trigger.wenn ?? '-'}, ${trigger.und ?? '-'}: ${trigger.dann ?? 0}%`)
      .join('\n');
    const zones = this.analysisData?.basisAnalyse?.wichtigeZonen?.join(', ') || '-';

    return [
      `Analyse: ${this.analysisData?.instrument ?? '-'} (${this.analysisData?.datumanalyse ?? '-'})`,
      `Variante: ${variant.name ?? '-'} | Trefferchance: ${variant.wahrscheinlichkeit ?? 0}%`,
      `Szenario: ${variant.szenario ?? '-'} | Richtung: ${this.analysisDirection(variant)}`,
      `Reaktionszone: ${variant.reaktionszone ?? '-'} | Invalidierung: ${variant.invalidierung ?? variant.stopLos ?? '-'}`,
      `Bestaetigung: ${variant.bestaetigung ?? '-'}`,
      `Wichtige Zonen: ${zones}`,
      '',
      variant.begruendung ?? '',
      triggers ? `\nTrigger:\n${triggers}` : '',
    ].filter((line) => line !== '').join('\n');
  }

  private buildSchemaAnalysisComment(variant: AnalysisVariant): string {
    const conditions = (variant.validity_conditions ?? [])
      .map((condition) => `- ${condition}`)
      .join('\n');
    const invalidation = (variant.invalidation ?? [])
      .map((item) => `- ${item}`)
      .join('\n');
    const risks = (variant.risks ?? [])
      .map((item) => `- ${item}`)
      .join('\n');
    const reasoning = (variant.reasoning ?? [])
      .map((item) => `- ${item}`)
      .join('\n');

    return [
      `Analyse: ${this.analysisData?.symbol ?? this.analysisData?.instrument ?? '-'} (${this.analysisData?.timeframe ?? '-'})`,
      `Variante: ${variant.variant_name ?? '-'} | Trefferchance: ${variant.probability_percent ?? 0}%`,
      `Setup: ${variant.setup_type ?? '-'} | Richtung: ${this.analysisDirection(variant)} | Status: ${variant.status ?? '-'}`,
      `Entry: ${variant.entry ?? '-'} | Stop: ${variant.stop_loss ?? '-'} | Ziel: ${variant.take_profit ?? '-'} | CRV: ${variant.risk_reward_ratio ?? '-'}`,
      `Positionsgroesse: ${variant.position_size?.quantity ?? '-'} ${variant.position_size?.unit ?? ''}`,
      '',
      'Was muss passieren?',
      conditions || '-',
      '',
      'Begruendung:',
      reasoning || '-',
      invalidation ? `\nInvalidierung:\n${invalidation}` : '',
      risks ? `\nRisiken:\n${risks}` : '',
    ].filter((line) => line !== '').join('\n');
  }

  private processScreenshotFile(file: File): void {
    this.screenshotUploadError = '';
    if (!this.isImageFile(file)) {
      this.screenshotUploadError = 'Nur Bilddateien (z. B. PNG, JPG, GIF) sind erlaubt.';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result?.toString();
      if (result) {
        this.screenshotUploadDataUrl = result;
        this.screenshotUploadName = file.name || 'Screenshot';
        this.screenshotUploadError = '';
      } else {
        this.screenshotUploadError = 'Screenshot konnte nicht geladen werden.';
      }
    };
    reader.onerror = () => {
      this.screenshotUploadError = 'Screenshot konnte nicht gelesen werden.';
    };
    reader.readAsDataURL(file);
  }

  private processEvaluationMediaFile(file: File): void {
    this.evaluationMediaUploadError = '';
    if (!this.isImageFile(file)) {
      this.evaluationMediaUploadError = 'Nur Bilddateien (z. B. PNG, JPG, GIF) sind erlaubt.';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result?.toString();
      if (result) {
        this.evaluationMediaUploadDataUrl = result;
        this.evaluationMediaUploadName = file.name || 'Screenshot';
        this.evaluationMediaUploadError = '';
      } else {
        this.evaluationMediaUploadError = 'Bild konnte nicht geladen werden.';
      }
    };
    reader.onerror = () => {
      this.evaluationMediaUploadError = 'Bild konnte nicht gelesen werden.';
    };
    reader.readAsDataURL(file);
  }

  private clearEvaluationMediaForm(): void {
    this.evaluationMediaUrl = '';
    this.evaluationMediaUploadName = '';
    this.evaluationMediaUploadError = '';
    this.evaluationMediaUploadDataUrl = '';
    this.evaluationMediaDropActive = false;
  }

  private processStornoScreenshotFile(file: File): void {
    this.stornoScreenshotUploadError = '';
    if (!this.isImageFile(file)) {
      this.stornoScreenshotUploadError = 'Nur Bilddateien (z. B. PNG, JPG, GIF) sind erlaubt.';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result?.toString();
      if (result) {
        this.stornoScreenshotUploadDataUrl = result;
        this.stornoScreenshotUploadName = file.name || 'Screenshot';
        this.stornoScreenshotUploadError = '';
      } else {
        this.stornoScreenshotUploadError = 'Screenshot konnte nicht geladen werden.';
      }
    };
    reader.onerror = () => {
      this.stornoScreenshotUploadError = 'Screenshot konnte nicht gelesen werden.';
    };
    reader.readAsDataURL(file);
  }

  private hasScreenshotInput(): boolean {
    return !!this.tradeForm.value.screenshotUrl?.toString().trim() || !!this.screenshotUploadDataUrl;
  }

  private isImageFile(file: File): boolean {
    if (file.type) {
      return file.type.startsWith('image/');
    }
    const extension = file.name?.split('.').pop()?.toLowerCase();
    if (!extension) return false;
    const allowed = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'tiff', 'svg', 'heic', 'heif', 'avif'];
    return allowed.includes(extension);
  }

  private parseFieldTextImages(fieldText: FieldText): FieldTextImage[] {
    if (!fieldText.bilderJson?.trim()) return [];

    try {
      const parsed = JSON.parse(fieldText.bilderJson);
      const images = Array.isArray(parsed) ? parsed : parsed?.images;
      if (!Array.isArray(images)) return [];

      return images
        .map((item, index) => ({
          id: item?.id?.toString() || `image-${index}`,
          name: item?.name?.toString() || `Bild ${index + 1}`,
          imageDataUrl: item?.imageDataUrl?.toString() || ''
        }))
        .filter((item) => !!item.imageDataUrl);
    } catch {
      return [];
    }
  }

  private parseFieldTextLinks(fieldText: FieldText): FieldTextLink[] {
    if (!fieldText.bilderJson?.trim()) return [];

    try {
      const parsed = JSON.parse(fieldText.bilderJson);
      const links = Array.isArray(parsed) ? [] : parsed?.links;
      if (!Array.isArray(links)) return [];

      return links
        .map((item, index) => ({
          id: item?.id?.toString() || `link-${index}`,
          label: item?.label?.toString() || item?.url?.toString() || `Link ${index + 1}`,
          url: item?.url?.toString() || ''
        }))
        .filter((item) => !!item.url);
    } catch {
      return [];
    }
  }

  private async loadExchangeRate(currency: TradeCurrency): Promise<void> {
    if (currency === 'EUR') {
      this.exchangeRates.EUR = 1;
      this.recalculatePositionAndProfit('init');
      return;
    }

    try {
      const rate = await firstValueFrom(this.api.getExchangeRate(currency));
      this.exchangeRates[currency] = rate && rate > 0 ? rate : 1;
    } catch {
      this.exchangeRates[currency] = 1;
    }
    this.recalculatePositionAndProfit('init');
  }

  private adjustedPositionSize(size: number): number {
    const currency = this.tradeForm.value.currency as TradeCurrency;
    if (currency === 'EUR') {
      return size;
    }
    const rate = this.exchangeRates[currency] ?? 1;
    return rate > 0 ? Math.floor(size / rate) : size;
  }

  private convertToEuro(value: number, currency?: TradeCurrency): number {
    if (currency !== 'USD') return value;
    const rate = this.exchangeRates.USD || 1;
    return rate > 0 ? value * rate : value;
  }

  private checkPositionSize(): void {
    const maxSize = this.maxPositionSize();
    const size = Number(this.tradeForm.value.size ?? 0);
    if (maxSize > 0 && size > maxSize) {
      this.warningMessage = 'Hinweis, die Positionsgröße wurde überschritten.';
    }
  }

  async submitStornoDetails(): Promise<void> {
    if (!this.stornoDialogEntryId) return;
    await this.stornoEntry(this.stornoDialogEntryId);
  }

  async stornoEntry(id: string): Promise<void> {
    const entry = this.entries().find((item) => item.id === id);
    if (!entry) return;
    const screenshots: TradeScreenshot[] = [...(entry.screenshots ?? [])];
    const trimmedUrl = this.stornoScreenshotUrl.trim();
    if (trimmedUrl) {
      screenshots.push({ id: uuid(), url: trimmedUrl, indexOrder: screenshots.length });
    }
    if (this.stornoScreenshotUploadDataUrl) {
      screenshots.push({ id: uuid(), imageDataUrl: this.stornoScreenshotUploadDataUrl, indexOrder: screenshots.length });
    }

    const updatedEntry: TradeEntry = {
      ...entry,
      status: 'Storniert',
      positioned: false,
      screenshots,
    };

    try {
      const saved = await this.saveEntry(updatedEntry);
      const updated = this.entries().map((item) => (item.id === id ? saved : item));
      this.entries.set(updated);
      this.updatePositionedPnl();
      this.ensurePageWithinBounds();
      this.ensureWatchPageWithinBounds();
      this.closeStornoDialog();
    } catch {
      // Fehler bereits angezeigt
    }
  }

  async submitSellDetails(): Promise<void> {
    if (!this.sellDialogEntryId || this.sellForm.invalid) return;

    const entry = this.entries().find((item) => item.id === this.sellDialogEntryId);
    if (!entry) return;

    const {
      sellPrice,
      sellSize,
      sellDate,
      sellScreenshotUrl,
      sellComment,
      sellFee,
      planFollowed,
      entryWasValid,
      exitWasValid,
      stopWasRespected,
      positionSizeWasCorrect,
      ruleBroken
    } = this.sellForm.value;
    const finalComment = sellComment?.toString().trim() || undefined;
    const partialSale: PartialSale = {
      id: uuid(),
      price: Number(sellPrice),
      size: Number(sellSize),
      date: sellDate || this.today(),
      pnl: this.calculateRealizedPnl(entry, Number(sellPrice), Number(sellSize), Number(sellFee)),
      realizedR: 0,
      comment: finalComment,
      fee: Number(sellFee) || 0,
      screenshots: [],
    };
    partialSale.realizedR = this.partialRealizedR(entry, partialSale);

    const saleScreenshots: TradeScreenshot[] = [];
    const trimmedUrl = sellScreenshotUrl?.toString().trim();
    if (trimmedUrl) {
      saleScreenshots.push({ id: uuid(), url: trimmedUrl, indexOrder: 0, partialSaleId: partialSale.id });
    }
    partialSale.screenshots = saleScreenshots;

    let savedPartialSale: PartialSale;
    try {
      savedPartialSale = await firstValueFrom(this.api.createPartialSale(this.sellDialogEntryId, partialSale));
    } catch (error) {
      this.showError('Teilverkauf konnte nicht gespeichert werden.', error);
      return;
    }

    const finalPartialSale: PartialSale = { ...partialSale, ...savedPartialSale };

    const updated = this.entries().map((current) => {
      if (current.id !== this.sellDialogEntryId) return current;
      const partialSales = [...(current.partialSales ?? []), finalPartialSale];
      const screenshots: TradeScreenshot[] = [...(current.screenshots ?? [])];
      const mergedScreenshots: TradeScreenshot[] = [...(finalPartialSale.screenshots ?? [])];
      if (mergedScreenshots.length) {
        const newIndex = screenshots.length;
        mergedScreenshots.forEach((shot, idx) => {
          screenshots.push({ ...shot, indexOrder: newIndex + idx, partialSaleId: finalPartialSale.id });
        });
      }
      const totalSold = partialSales.reduce((sum, sale) => sum + sale.size, 0);
      const realizedPnl = partialSales.reduce((sum, sale) => sum + sale.pnl, 0);
      const isClosed = totalSold >= current.size;
      const status: TradeState = isClosed ? 'Glatt' : 'Teilverkauf';
      const lastSale = partialSales[partialSales.length - 1];
      const nextEntry = {
        ...current,
        status,
        positioned: isClosed ? false : true,
        sellDate: lastSale.date,
        sellPrice: lastSale.price,
        sellSize: lastSale.size,
        sellFee: lastSale.fee ?? current.sellFee ?? 0,
        realizedPnl,
        partialSales,
        comment: finalPartialSale.comment || current.comment,
        screenshots,
        reviewRequired: isClosed,
        reviewCompleted: current.reviewCompleted,
        reviewCompletedAt: current.reviewCompletedAt,
        planFollowed,
        entryWasValid,
        exitWasValid,
        stopWasRespected,
        positionSizeWasCorrect,
        ruleBroken,
      };
      return this.withLearningMetrics(nextEntry);
    });

    const changed = updated.find((e) => e.id === this.sellDialogEntryId);
    try {
      const saved = changed
        ? await firstValueFrom(this.api.updateTradeEntry(this.sellDialogEntryId, changed))
        : null;
      const normalized = saved ? this.normalizeEntry(saved) : null;
      const finalList = saved ? updated.map((entry) => (entry.id === this.sellDialogEntryId ? saved : entry)) : this.entries();
      if (normalized) {
        const normalizedList = finalList.map((entry) => (entry.id === this.sellDialogEntryId ? normalized : entry));
        this.entries.set(normalizedList);
        this.closeSellDialog();
        this.updatePositionedPnl();
        this.ensurePageWithinBounds();
      }
    } catch {
      // Fehler bereits angezeigt
    }
  }

  closeSellDialog(): void {
    this.sellDialogEntryId = null;
    this.sellDialogMode = null;
    this.sellForm.reset({
      sellPrice: 0,
      sellSize: 0,
      sellDate: this.today(),
      sellScreenshotUrl: '',
      sellFee: 0,
      sellComment: '',
      rulesFollowed: null,
      planFollowed: null,
      entryWasValid: null,
      exitWasValid: null,
      stopWasRespected: null,
      positionSizeWasCorrect: null,
      ruleBroken: null
    });
  }

  private setBinaryChoice(form: FormGroup, controlName: string, value: boolean): void {
    const control = form.get(controlName);
    if (!control) return;
    const current = control.value as boolean | null;
    control.setValue(current === value ? null : value);
  }

  private syncRulesFollowedComment(): void {
    const value = this.sellForm.get('rulesFollowed')?.value as boolean | null;
    const commentControl = this.sellForm.get('sellComment');
    if (!commentControl) return;
    const raw = commentControl.value?.toString() ?? '';
    const lines = raw
      .split('\n')
      .map((line: string) => line.trim())
      .filter((line: string) => line.length > 0 && !line.startsWith('Regeln eingehalten:'));
    if (value === true || value === false) {
      lines.push(`Regeln eingehalten: ${value ? 'Ja' : 'Nein'}`);
    }
    commentControl.setValue(lines.join('\n'));
  }

  private buildDailyCheckBlock(
    structureIntact: boolean,
    assumptionViolated: boolean,
    stopCorrect: boolean,
    earningsSoon: boolean,
    rulesFollowed: boolean,
    isLongTerm: boolean,
    longTermTrendIntact?: boolean,
    longTermRsiExtreme?: boolean,
    longTermStopCorrect?: boolean,
  ): string {
    const lines = [
      `[${this.today()}]`,
      `Ist die Struktur noch intakt? ${structureIntact ? 'Ja' : 'Nein'}`,
      `Wurde meine Annahme verletzt? ${assumptionViolated ? 'Ja' : 'Nein'}`,
      `Ist der Stop/Loss noch richtig? ${stopCorrect ? 'Ja' : 'Nein'}`,
      `Stehen Earnings kurzfristig an? ${earningsSoon ? 'Ja' : 'Nein'}`,
      `Habe ich alle Regeln eingehalten? ${rulesFollowed ? 'Ja' : 'Nein'}`,
    ];

    if (isLongTerm) {
      lines.push(`Trend weiterhin intakt? ${longTermTrendIntact ? 'Ja' : 'Nein'}`);
      lines.push(`RSI überkauft oder überverkauft? ${longTermRsiExtreme ? 'Ja' : 'Nein'}`);
      if (longTermRsiExtreme) {
        lines.push(`Ist der Stop/Loss noch richtig? ${longTermStopCorrect ? 'Ja' : 'Nein'}`);
      }
    }

    return lines.join('\n');
  }

  private syncDailyCheckComment(): void {
    this.syncDailyCheckValidators();
    const commentControl = this.dailyCheckForm.get('comment');
    if (!commentControl) return;
    const {
      structureIntact,
      assumptionViolated,
      stopCorrect,
      earningsSoon,
      rulesFollowed,
      longTermTrendIntact,
      longTermRsiExtreme,
      longTermStopCorrect,
    } = this.dailyCheckForm.value;
    const isLongTerm = this.isDailyCheckLongTerm();
    const needsLongTermStop = isLongTerm && longTermRsiExtreme === true;

    if (
      structureIntact === null
      || assumptionViolated === null
      || stopCorrect === null
      || earningsSoon === null
      || rulesFollowed === null
      || (isLongTerm && (longTermTrendIntact === null || longTermRsiExtreme === null))
      || (needsLongTermStop && longTermStopCorrect === null)
    ) {
      return;
    }
    const block = this.buildDailyCheckBlock(
      structureIntact,
      assumptionViolated,
      stopCorrect,
      earningsSoon,
      rulesFollowed,
      isLongTerm,
      longTermTrendIntact,
      longTermRsiExtreme,
      longTermStopCorrect,
    );
    const base = this.stripTodayDailyCheckBlock(this.dailyCheckBaseComment).trimEnd();
    const combined = base ? `${base}\n\n${block}` : block;
    commentControl.setValue(combined);
  }

  private stripTodayDailyCheckBlock(value: string): string {
    const blockPrefix = `[${this.today()}]`;
    if (!value.includes(blockPrefix)) return value;
    const lines = value.split('\n');
    for (let i = lines.length - 1; i >= 0; i -= 1) {
      if (lines[i].trim() !== blockPrefix) continue;
      const dailyPrefixes = [
        'Ist die Struktur noch intakt?',
        'Wurde meine Annahme verletzt?',
        'Ist der Stop/Loss noch richtig?',
        'Stehen Earnings kurzfristig an?',
        'Habe ich alle Regeln eingehalten?',
        'Trend weiterhin intakt?',
        'RSI überkauft oder überverkauft?',
      ];
      let end = i + 1;
      while (dailyPrefixes.some((prefix) => lines[end]?.startsWith(prefix))) {
        end += 1;
      }
      return [...lines.slice(0, i), ...lines.slice(end)].join('\n');
    }
    return value;
  }

  toggleRiskPanel(): void {
    this.riskPanelCollapsed = !this.riskPanelCollapsed;
  }

  toggleChatPanel(): void {
    this.chatPanelCollapsed = !this.chatPanelCollapsed;
  }

  togglePromptPanel(): void {
    this.promptPanelVisible = !this.promptPanelVisible;
  }

  choosePromptForEdit(id: string): void {
    this.selectedPromptId = id;
    const chosen = this.aiPrompts.find((p) => p.id === id);
    if (chosen) {
      this.editPromptName = chosen.name;
      this.tradeForm.patchValue({ aiSystemPrompt: chosen.text });
    }
  }

  toggleEntryPanel(): void {
    this.entryPanelCollapsed = !this.entryPanelCollapsed;
  }

  toggleQuestionPanel(panel: 'setup' | 'emotion' | 'technical'): void {
    if (panel === 'setup') {
      this.setupQuestionsCollapsed = !this.setupQuestionsCollapsed;
      return;
    }
    if (panel === 'emotion') {
      this.emotionQuestionsCollapsed = !this.emotionQuestionsCollapsed;
      return;
    }
    this.technicalQuestionsCollapsed = !this.technicalQuestionsCollapsed;
  }

  async selectPrompt(id: string): Promise<void> {
    this.selectedPromptId = id;
    const chosen = this.aiPrompts.find((p) => p.id === id);
    if (!chosen) return;
    this.editPromptName = chosen.name;
    this.tradeForm.patchValue({ aiSystemPrompt: chosen.text });
    this.aiStatus = `Prompt "${chosen.name}" geladen.`;
  }

  async savePrompt(): Promise<void> {
    const name = this.newPromptName.trim();
    const systemPrompt = (this.tradeForm.value.aiSystemPrompt ?? '').toString().trim();
    if (!name || !systemPrompt) {
      this.aiStatus = 'Bitte Namen und Anweisungen ausfüllen.';
      return;
    }

    const sampleText = 'Long   kauf 10:00 stop9,80';
    this.aiStatus = 'Validiere Prompt mit ChatGPT…';
    let validationPassed = true;
    try {
      const preview = await this.sendPromptToChatGpt(sampleText, systemPrompt || undefined);
      this.aiStatus = preview ? `ChatGPT-Vorschau: ${preview}` : 'ChatGPT-Antwort erhalten.';
    } catch (error) {
      const detail = this.formatError(error);
      this.aiStatus = (detail || 'ChatGPT konnte nicht erreicht werden.') + ' Prompt wird trotzdem gespeichert.';
      validationPassed = false;
    }

    const existing = this.aiPrompts.find((p) => p.name.toLowerCase() === name.toLowerCase());
    if (existing) {
      const updatedTemplate: AiPromptTemplate = { ...existing, name, text: systemPrompt, systemPrompt: systemPrompt };
      try {
        const saved = await firstValueFrom(this.api.updateAiPromptTemplate(existing.id, updatedTemplate));
        this.aiPrompts = this.aiPrompts.map((p) => (p.id === existing.id ? saved : p));
        this.selectedPromptId = saved.id;
        this.editPromptName = saved.name;
      } catch {
        this.aiStatus = 'Prompt konnte nicht gespeichert werden.';
        this.showError('Prompt konnte nicht gespeichert werden.', 'Update fehlgeschlagen.');
        return;
      }
    } else {
      const created: AiPromptTemplate = { id: uuid(), name, text: systemPrompt, systemPrompt: systemPrompt };
      try {
        const saved = await firstValueFrom(this.api.createAiPromptTemplate(created));
        this.aiPrompts = [...this.aiPrompts, saved];
        this.selectedPromptId = saved.id;
        this.editPromptName = saved.name;
      } catch {
        this.aiStatus = 'Prompt konnte nicht gespeichert werden.';
        this.showError('Prompt konnte nicht gespeichert werden.', 'Erstellen fehlgeschlagen.');
        return;
      }
    }

    this.aiStatus = validationPassed
      ? `Prompt "${name}" gespeichert.`
      : `Prompt "${name}" gespeichert (ohne ChatGPT-Validierung).`;
    this.newPromptName = '';
    this.editPromptName = '';
    this.promptPanelVisible = false;
  }

  async updatePrompt(): Promise<void> {
    const id = this.selectedPromptId;
    const name = this.editPromptName.trim();
    const systemPrompt = (this.tradeForm.value.aiSystemPrompt ?? '').toString().trim();
    if (!id || !name || !systemPrompt) {
      this.aiStatus = 'Bitte einen gespeicherten Prompt auswählen und Name/Anweisungen ausfüllen.';
      return;
    }

    const performUpdate = async () => {
      const current = this.aiPrompts.find((p) => p.id === id);
      if (!current) return;

      const updated: AiPromptTemplate = { ...current, name, text: systemPrompt, systemPrompt };
      try {
        const saved = await firstValueFrom(this.api.updateAiPromptTemplate(id, updated));
        this.aiPrompts = this.aiPrompts.map((p) => (p.id === id ? saved : p));
        this.aiStatus = `Prompt "${name}" aktualisiert.`;
        this.promptPanelVisible = false;
      } catch (error) {
        this.showError('Prompt konnte nicht aktualisiert werden.', error);
      }
    };

    this.openConfirm(`Prompt "${name}" wirklich aktualisieren?`, performUpdate);
  }

  async deletePrompt(): Promise<void> {
    const id = this.selectedPromptId;
    if (!id) {
      this.aiStatus = 'Bitte einen gespeicherten Prompt auswählen.';
      return;
    }

    const current = this.aiPrompts.find((p) => p.id === id);
    const performDelete = async () => {
      try {
        await firstValueFrom(this.api.deleteAiPromptTemplate(id));
        this.aiPrompts = this.aiPrompts.filter((p) => p.id !== id);
        this.selectedPromptId = this.aiPrompts[0]?.id ?? null;
        const fallback = this.aiPrompts.find((p) => p.id === this.selectedPromptId);
        this.editPromptName = fallback?.name ?? '';
        this.tradeForm.patchValue({ aiSystemPrompt: fallback?.text ?? '' });
        this.aiStatus = 'Prompt gelöscht.';
        this.promptPanelVisible = false;
      } catch (error) {
        this.showError('Prompt konnte nicht gelöscht werden.', error);
      }
    };

    this.openConfirm(`Prompt "${current?.name ?? ''}" wirklich löschen?`, performDelete);
  }

  async addTrade(): Promise<void> {
    if (this.tradeForm.invalid) return;
    if (this.analysisData && !this.analysisPersistedId) {
      if (this.analysisRawJson) {
        await this.persistAnalysisFile(this.analysisFileName || 'analyse.json', this.analysisRawJson);
      }
      if (!this.analysisPersistedId) {
        this.showError('Analyse muss vor dem Speichern des Trades in der Datenbank gespeichert sein.');
        return;
      }
    }
    const formValue = this.tradeForm.value;
    const entryDate = formValue.positioned && !formValue.entryDate ? this.today() : formValue.entryDate!;
    const tradeType = formValue.tradeType?.toString() || 'Aktie';

    const screenshots: TradeScreenshot[] = [];
    const baseScreenshot = formValue.screenshotUrl?.toString().trim();
    if (baseScreenshot) {
      screenshots.push({ id: uuid(), url: baseScreenshot, indexOrder: 0 });
    }
    if (this.screenshotUploadDataUrl) {
      screenshots.push({ id: uuid(), imageDataUrl: this.screenshotUploadDataUrl, indexOrder: screenshots.length });
    }
    const selectedVariant = this.selectedAnalysisVariant;

    const newEntry: TradeEntry = this.withLearningMetrics({
      id: uuid(),
      feldTextId: this.activeJournalArea?.feldTextId || this.defaultArea().feldTextId,
      analysisId: this.analysisPersistedId || undefined,
      selectedVariantName: selectedVariant ? this.variantName(selectedVariant) : undefined,
      selectedVariantIndex: this.selectedAnalysisVariantIndex ?? undefined,
      selectedVariantProbability: selectedVariant ? Number(selectedVariant.probability_percent ?? selectedVariant.wahrscheinlichkeit ?? 0) : undefined,
      selectedVariantTrend: selectedVariant ? this.analysisDirection(selectedVariant).toLowerCase() : undefined,
      selectedVariantScenario: selectedVariant?.setup_type ?? selectedVariant?.szenario,
      selectedVariantStatus: selectedVariant?.status ?? selectedVariant?.statusDerVariante,
      selectedVariantTriggerJson: selectedVariant?.trigger?.length ? JSON.stringify(selectedVariant.trigger) : undefined,
      agentSchemaVersion: this.analysisData ? (this.analysisData.trade_variants?.length ? 'trade_variants' : 'agent-varianten') : undefined,
      agentPromptVersion: this.analysisData?.bildValidierung ? 'tradingview-analysis-agent-v9' : this.analysisData ? 'agent-v2' : undefined,
      triggerFulfilled: selectedVariant ? 'unbekannt' : undefined,
      analysisOutcome: selectedVariant ? 'unbekannt' : undefined,
      symbol: formValue.symbol!,
      wknIsin: formValue.wknIsin?.toString().toUpperCase() || undefined,
      currency: (formValue.currency as TradeCurrency) || 'EUR',
      certificate: this.isCertificateType(tradeType) || !!formValue.certificate,
      tradeType,
      crvGrund: this.selectedCrvRatio(),
      marge: this.isCfdType(tradeType) ? this.validNumber(formValue.marge) ?? 0 : 0,
      positioned: !!formValue.positioned,
      status: !!formValue.positioned ? 'Positioniert' : 'Watch',
      entryDate,
      sellDate: '',
      sellPrice: undefined,
      sellSize: undefined,
      entryFee: Number(formValue.entryFee) || 0,
      sellFee: 0,
      realizedPnl: 0,
      direction: formValue.direction as TradeDirection,
      setup: formValue.setup!,
      entryPrice: Number(formValue.entryPrice),
      stopPrice: Number(formValue.stopPrice),
      targetPrice: Number(formValue.targetPrice),
      size: Number(formValue.size),
      comment: formValue.comment!,
      screenshots
    });

    try {
      const savedEntry = await this.saveEntry(newEntry, true);
      const updated = [...this.entries(), savedEntry];
      this.entries.set(updated);
      this.ensurePageWithinBounds();
      this.ensureWatchPageWithinBounds();
      this.tradeForm.reset({
        symbol: '',
        wknIsin: '',
        positioned: false,
        entryDate: '',
        sellDate: '',
        direction: 'Long',
        certificate: false,
        tradeType: 'CFD',
        setup: this.setups[0] ?? 'Ausbruch',
        currency: 'EUR',
        crvGrund: 3,
        marge: 0,
        entryPrice: 0,
        stopPrice: 0,
        targetPrice: 0,
        size: 0,
        entryFee: 0,
        aiPrompt: this.tradeForm.value.aiPrompt,
        aiSystemPrompt: this.tradeForm.value.aiSystemPrompt,
        questionHigherTimeframe: null,
        questionHigherTimeframeConfirmsTrend: null,
        questionSupportResistance: null,
        questionEarningsSoon: null,
        questionSetupConfidence: null,
        questionMotivation: null,
        questionEmotionalState: '',
        questionRiskManagement: null,
        questionLastTradesPositive: null,
        questionReviewedLastTrades: null,
        questionEma200: null,
        questionRsiUnder60: null,
        questionRsiOverbought: null,
        questionTrendClear: null,
        questionSignalCandle: null,
        questionVolumeRising: null,
        questionRecommendedStock: null,
        questionHighlySpeculative: null,
        questionLowVolumeMoves: null,
        comment: '',
        screenshotUrl: ''
      });
      this.cfdEntryFeeManuallyEdited = false;
      this.manualComment = '';
      this.screenshotUploadDataUrl = '';
      this.screenshotUploadName = '';
      this.screenshotUploadError = '';
      this.analysisData = null;
      this.analysisDialogOpen = false;
      this.analysisFileName = '';
      this.analysisRawJson = '';
      this.analysisPersistedId = '';
      this.analysisUploadError = '';
      this.analysisImageDropActive = false;
      this.analysisImageUploadName = '';
      this.analysisImageRunning = false;
      this.selectedAnalysisVariant = null;
      this.selectedAnalysisVariantIndex = null;
      this.onTradeTypeChange();
      this.recalculatePositionAndProfit('init');
      this.updatePositionedPnl();
    } catch {
      // Fehler bereits angezeigt, Formularwerte bleiben erhalten
    }
  }

  async deleteEntry(id: string): Promise<void> {
    if (this.editingEntryId === id) {
      this.cancelEdit();
    }
    const updated = this.entries().filter((entry) => entry.id !== id);
    this.entries.set(updated);
    await this.deleteEntryRemote(id);
    this.updatePositionedPnl();
    this.ensurePageWithinBounds();
  }

  displayR(entry: TradeEntry): number {
    if (entry.status === 'Glatt' || (entry.partialSales?.length ?? 0) > 0) {
      return this.realizedDisplayR(entry);
    }
    return 0;
  }

  hasRealizedR(entry: TradeEntry): boolean {
    return entry.status === 'Glatt' || (entry.partialSales?.length ?? 0) > 0;
  }

  realizedResultR(entry: TradeEntry): number {
    if (entry.status !== 'Glatt' && !(entry.partialSales?.length ?? 0)) return 0;
    return this.realizedDisplayR(entry);
  }

  tableColumnCount(): number {
    return this.showFinancialColumns ? 19 : 15;
  }

  remainingSize(entry: TradeEntry): number {
    const sold = (entry.partialSales ?? []).reduce((sum, sale) => sum + sale.size, 0);
    const remaining = entry.size - sold;
    return remaining > 0 ? remaining : 0;
  }

  partialSummary(entry: TradeEntry): string {
    const sold = (entry.partialSales ?? []).reduce((sum, sale) => sum + sale.size, 0);
    if (!sold) return '';
    return `Teilverkauf: ${sold}/${entry.size} Stück verkauft`;
  }

  saleLabel(entry: TradeEntry, sale: PartialSale, index: number): string {
    const allSales = entry.partialSales ?? [];
    const soldUpTo = allSales.slice(0, index + 1).reduce((sum, s) => sum + s.size, 0);
    if (this.isStopSale(entry, sale)) {
      return 'Stop';
    }
    if (soldUpTo >= entry.size) {
      return 'Verkauf';
    }
    return `Verkauf ${index + 1}`;
  }

  screenshotLines(entry: TradeEntry): Array<{ label: string; url: string; type: 'link' | 'image' }> {
    const lines: Array<{ label: string; url: string; type: 'link' | 'image' }> = [];
    const sortedShots = (entry.screenshots ?? []).slice().sort((a, b) => a.indexOrder - b.indexOrder);
    const baseShots = sortedShots.filter((shot) => !this.shotSaleId(shot));

    baseShots.forEach((shot, idx) => {
      const target = this.screenshotTarget(shot);
      if (target.url) {
        const label = entry.status === 'Storniert' && idx === baseShots.length - 1
          ? 'Storno'
          : baseShots.length > 1 ? `Kauf ${idx + 1}` : 'Kauf';
        lines.push({ label, ...target });
      }
    });

    (entry.partialSales ?? []).forEach((sale, index) => {
      const saleShots = this.saleScreenshots(sortedShots, sale);
      saleShots.forEach((shot, shotIdx) => {
        const baseLabel = this.saleLabel(entry, sale, index);
        const label = saleShots.length > 1 ? `${baseLabel} ${shotIdx + 1}` : baseLabel;
        const target = this.screenshotTarget(shot);
        if (target.url) {
          lines.push({ label, ...target });
        }
      });
    });

    return lines;
  }

  screenshotPreviewLines(entry: TradeEntry): Array<{ label: string; url: string; type: 'link' | 'image' }> {
    const lines = this.screenshotLines(entry);
    if (this.screenshotsVisible(entry.id)) {
      return lines;
    }
    return lines.slice(0, 1);
  }

  hasMultipleScreenshots(entry: TradeEntry): boolean {
    return this.screenshotLines(entry).length > 1;
  }

  private shotSaleId(shot: TradeScreenshot): string | undefined {
    return shot.partialSaleId || (shot as any).partialSale?.id;
  }

  private screenshotTarget(shot: TradeScreenshot): { url: string; type: 'link' | 'image' } {
    if (shot.url) {
      return { url: shot.url, type: 'link' };
    }
    if (shot.hasImageDataUrl) {
      return { url: this.api.screenshotImageUrl(shot.id), type: 'image' };
    }
    if (shot.imageDataUrl) {
      return { url: shot.imageDataUrl, type: 'image' };
    }
    return { url: '', type: 'link' };
  }

  private saleScreenshots(shots: TradeScreenshot[], sale: PartialSale): TradeScreenshot[] {
    if (sale.screenshots?.length) {
      return sale.screenshots;
    }
    return shots.filter((shot) => this.shotSaleId(shot) === sale.id);
  }

  private isStopSale(entry: TradeEntry, sale: PartialSale): boolean {
    return this.calculationDirection(entry) === 'Long'
      ? sale.price < entry.entryPrice
      : sale.price > entry.entryPrice;
  }

  partialPnlSum(entry: TradeEntry): number {
    return (entry.partialSales ?? []).reduce((sum, sale) => sum + sale.pnl, 0);
  }

  togglePartialList(id: string): void {
    if (this.partialToggles.has(id)) {
      this.partialToggles.delete(id);
    } else {
      this.partialToggles.add(id);
    }
  }

  partialsVisible(id: string): boolean {
    return this.partialToggles.has(id);
  }

  toggleScreenshotList(id: string): void {
    if (this.screenshotToggles.has(id)) {
      this.screenshotToggles.delete(id);
    } else {
      this.screenshotToggles.add(id);
    }
  }

  screenshotsVisible(id: string): boolean {
    return this.screenshotToggles.has(id);
  }

  startEdit(entry: TradeEntry): void {
    this.editingEntryId = entry.id;
    this.editingValues = {
      entryPrice: entry.entryPrice,
      stopPrice: entry.stopPrice,
      targetPrice: entry.targetPrice,
      size: entry.size,
      entryFee: entry.entryFee ?? 0,
      comment: entry.comment ?? '',
      stopChangeReason: ''
    };
  }

  cancelEdit(): void {
    this.editingEntryId = null;
  }

  async saveEdit(id: string): Promise<void> {
    if (!this.editingEntryId || this.editingEntryId !== id) return;

    const { entryPrice, stopPrice, size, entryFee, comment, stopChangeReason } = this.editingValues;
    if (entryPrice <= 0 || stopPrice <= 0 || size <= 0) {
      return;
    }

    const originalEntry = this.entries().find((entry) => entry.id === id);
    if (!originalEntry) return;
    if (Number(stopPrice) !== originalEntry.stopPrice && !stopChangeReason.trim()) {
      this.warningMessage = 'Bitte begründe die Stop-Änderung oder setze den ursprünglichen Stop wieder ein.';
      return;
    }

    const updated = this.entries().map((entry) => {
      if (entry.id !== id) return entry;
      const newStop = Number(stopPrice);
      const previousStop = entry.stopPrice;
      const updatedComment = this.appendStopComment(comment, previousStop, newStop, stopChangeReason);
      return {
        ...entry,
        entryPrice: Number(entryPrice),
        stopPrice: newStop,
        stopPricePrevious: newStop !== previousStop ? previousStop : entry.stopPricePrevious,
        targetPrice: entry.targetPrice,
        size: Number(size),
        entryFee: Number(entryFee) || 0,
        comment: updatedComment,
      };
    });

    const changed = updated.find((e) => e.id === id);
    try {
      const saved = changed ? await this.saveEntry(changed) : null;
      if (saved) {
        const finalList = updated.map((entry) => (entry.id === id ? saved : entry));
        this.entries.set(finalList);
        this.updatePositionedPnl();
        this.cancelEdit();
      }
    } catch {
      // Fehler bereits angezeigt, Edit-Modus bleibt offen
    }
  }

  commentLinks(comment: string | undefined): string[] {
    const value = (comment ?? '').toString();
    const matches = value.match(/https?:\/\/[^\s<>"']+/g) ?? [];
    return [...new Set(matches.map((url) => url.replace(/[),.;!?]+$/, '')))];
  }

  hasCommentInfo(entry: TradeEntry): boolean {
    return !!entry.comment?.toString().trim();
  }

  commentPreview(entry: TradeEntry): string {
    return entry.comment?.toString().trim() ?? '';
  }

  commentDetails(entry: TradeEntry): string {
    return this.commentDetailLine('Kommentar', entry.comment);
  }

  private commentDetailLine(label: string, value: string | undefined): string {
    const trimmed = value?.toString().trim();
    return trimmed ? `${label}:\n${trimmed}` : '';
  }

  showCommentTooltip(entry: TradeEntry, event: MouseEvent): void {
    const trimmed = this.commentDetails(entry).trim();
    if (!trimmed) return;
    this.commentTooltipText = trimmed;
    this.moveCommentTooltip(event);
  }

  moveCommentTooltip(event: MouseEvent): void {
    if (!this.commentTooltipText) return;
    const tooltipWidth = Math.min(560, Math.max(320, window.innerWidth * 0.45));
    const margin = 16;
    const preferredX = event.clientX + 14;
    const preferredY = event.clientY + 16;
    this.commentTooltipX = Math.min(preferredX, window.innerWidth - tooltipWidth - margin);
    this.commentTooltipY = Math.min(preferredY, window.innerHeight - 220);
    this.commentTooltipX = Math.max(margin, this.commentTooltipX);
    this.commentTooltipY = Math.max(margin, this.commentTooltipY);
  }

  hideCommentTooltip(): void {
    this.commentTooltipText = '';
  }

  changeSort(field: 'sellDate' | 'symbol' | 'entryDate' | 'status'): void {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDirection = 'asc';
    }
  }

  changeWatchSort(field: 'sellDate' | 'symbol' | 'entryDate' | 'status'): void {
    if (this.watchSortField === field) {
      this.watchSortDirection = this.watchSortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.watchSortField = field;
      this.watchSortDirection = 'asc';
    }
  }

  onPageSizeChange(size: number): void {
    const nextSize = Number(size) || this.pageSize;
    this.pageSize = nextSize;
    this.currentPage = 1;
    this.ensurePageWithinBounds();
  }

  onWatchPageSizeChange(size: number): void {
    const nextSize = Number(size) || this.watchPageSize;
    this.watchPageSize = nextSize;
    this.watchCurrentPage = 1;
    this.ensureWatchPageWithinBounds();
  }

  nextPage(): void {
    const total = this.totalPositionedPages();
    if (this.currentPage < total) {
      this.currentPage++;
    }
  }

  nextWatchPage(): void {
    const total = this.totalPlannedPages();
    if (this.watchCurrentPage < total) {
      this.watchCurrentPage++;
    }
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
    }
  }

  prevWatchPage(): void {
    if (this.watchCurrentPage > 1) {
      this.watchCurrentPage--;
    }
  }

  sortLabel(field: 'sellDate' | 'symbol' | 'entryDate' | 'status'): string {
    const direction = this.sortField === field ? (this.sortDirection === 'asc' ? '↑' : '↓') : '';
    return direction;
  }

  watchSortLabel(field: 'sellDate' | 'symbol' | 'entryDate' | 'status'): string {
    const direction = this.watchSortField === field ? (this.watchSortDirection === 'asc' ? '↑' : '↓') : '';
    return direction;
  }

  private sortedPositionedEntries(): TradeEntry[] {
    const filtered = this.filteredPositionedEntries();
    const sorted = [...filtered].sort((a, b) => {
      const openComparison = Number(this.isOpenPosition(b)) - Number(this.isOpenPosition(a));
      if (openComparison !== 0) return openComparison;

      let comparison = 0;
    if (this.sortField === 'sellDate') {
      const aDate = a.sellDate || '';
      const bDate = b.sellDate || '';
      comparison = aDate.localeCompare(bDate);
    } else if (this.sortField === 'entryDate') {
      const aDate = a.entryDate || '';
      const bDate = b.entryDate || '';
      comparison = aDate.localeCompare(bDate);
    } else if (this.sortField === 'status') {
      const aStatus = a.status || '';
      const bStatus = b.status || '';
      comparison = aStatus.localeCompare(bStatus);
    } else {
      comparison = a.symbol.localeCompare(b.symbol);
    }

      return this.sortDirection === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }

  private sortedPlannedEntries(): TradeEntry[] {
    const sorted = [...this.plannedEntries()].sort((a, b) => {
      let comparison = 0;
    if (this.watchSortField === 'sellDate') {
      const aDate = a.sellDate || '';
      const bDate = b.sellDate || '';
      comparison = aDate.localeCompare(bDate);
    } else if (this.watchSortField === 'entryDate') {
      const aDate = a.entryDate || '';
      const bDate = b.entryDate || '';
      comparison = aDate.localeCompare(bDate);
    } else if (this.watchSortField === 'status') {
      const aStatus = a.status || '';
      const bStatus = b.status || '';
      comparison = aStatus.localeCompare(bStatus);
    } else {
      comparison = a.symbol.localeCompare(b.symbol);
    }

      return this.watchSortDirection === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }

  private filteredPositionedEntries(): TradeEntry[] {
    return this.positionedEntries().filter((entry) => {
      const dateStr = entry.sellDate || entry.entryDate || '';
      if (this.yearFilter) {
        const year = dateStr ? new Date(dateStr).getFullYear() : null;
        if (year !== Number(this.yearFilter)) return false;
      }
      if (this.dateFrom) {
        if (!dateStr || new Date(dateStr) < new Date(this.dateFrom)) return false;
      }
      if (this.dateTo) {
        if (!dateStr || new Date(dateStr) > new Date(this.dateTo)) return false;
      }
      if (this.positionedStatusFilter) {
        if (entry.status !== this.positionedStatusFilter) return false;
      }
      return true;
    });
  }

  ensurePageWithinBounds(): void {
    const totalPages = this.totalPositionedPages();
    if (this.currentPage > totalPages) {
      this.currentPage = totalPages;
    }
    if (this.currentPage < 1) {
      this.currentPage = 1;
    }
  }

  ensureWatchPageWithinBounds(): void {
    const totalPages = this.totalPlannedPages();
    if (this.watchCurrentPage > totalPages) {
      this.watchCurrentPage = totalPages;
    }
    if (this.watchCurrentPage < 1) {
      this.watchCurrentPage = 1;
    }
  }

  private buildYearOptions(): string[] {
    const current = new Date();
    const start = 2025;
    const max = current.getFullYear() + (current.getMonth() === 11 ? 1 : 0);
    const years: string[] = [''];
    for (let y = start; y <= max; y++) {
      years.push(String(y));
    }
    return years;
  }

  private setupAutoCalculations(): void {
    const entryControl = this.tradeForm.get('entryPrice');
    const stopControl = this.tradeForm.get('stopPrice');

    entryControl?.valueChanges.subscribe(() => {
      if (this.isCfdSelected()) this.applyCfdStopFromTarget();
      this.recalculatePositionAndProfit('entry');
    });
    stopControl?.valueChanges.subscribe(() => this.recalculatePositionAndProfit('stop'));
    this.tradeForm.get('certificate')?.valueChanges.subscribe(() => this.recalculatePositionAndProfit('direction'));
    this.tradeForm.get('tradeType')?.valueChanges.subscribe(() => this.onTradeTypeChange());
    this.tradeForm.get('targetPrice')?.valueChanges.subscribe(() => {
      if (this.isCfdSelected()) this.applyCfdStopFromTarget();
      this.recalculatePositionAndProfit(this.isCfdSelected() ? 'risk' : 'target');
    });
    this.tradeForm.get('crvGrund')?.valueChanges.subscribe(() => {
      if (this.isCfdSelected()) this.applyCfdStopFromTarget();
      this.recalculatePositionAndProfit(this.isCfdSelected() ? 'risk' : 'target');
    });
    this.tradeForm.get('marge')?.valueChanges.subscribe(() => this.recalculatePositionAndProfit('risk'));
    this.recalculatePositionAndProfit('init');
  }

  isStopChanged(entry: TradeEntry): boolean {
    return Number(this.editingValues.stopPrice) !== Number(entry.stopPrice);
  }

  closeError(): void {
    this.errorMessage = '';
  }

  openConfirm(message: string, action: () => void | Promise<void>): void {
    this.confirmMessage = message;
    this.confirmAction = action;
  }

  closeConfirm(): void {
    this.confirmMessage = '';
    this.confirmAction = null;
  }

  async confirmProceed(): Promise<void> {
    if (this.confirmAction) {
      const toRun = this.confirmAction;
      this.closeConfirm();
      await toRun();
    }
  }

  private showError(message: string, error?: unknown): void {
    const detail = this.formatError(error);
    this.errorMessage = detail ? `${message} ${detail}` : message;
  }

  private formatError(error: unknown): string {
    if (!error) return '';
    if (error instanceof HttpErrorResponse) {
      const status = error.status ? `Status ${error.status}` : '';
      const text = error.error?.message || error.statusText || '';
      return `${status}${status && text ? ' - ' : ''}${text}`.trim();
    }
    if (error instanceof Error) {
      return error.message ? `(${error.message})` : '';
    }
    return '';
  }

  private calculateRealizedPnl(entry: TradeEntry, sellPrice: number, sellSize: number, sellFee = 0): number {
    if (sellPrice === undefined || sellSize <= 0) {
      return entry.realizedPnl ?? 0;
    }

    const perShare = this.calculationDirection(entry) === 'Long'
      ? sellPrice - entry.entryPrice
      : entry.entryPrice - sellPrice;

    const proportionalEntryFee = (entry.entryFee ?? 0) * (sellSize / (entry.size || sellSize || 1));
    const total = perShare * sellSize * this.priceMultiplier(entry) - proportionalEntryFee - (sellFee ?? 0);
    return +total.toFixed(2);
  }

  private initialRiskPerUnit(entry: TradeEntry): number {
    const initialStop = entry.initialStopPrice ?? entry.stopPricePrevious ?? entry.stopPrice;
    return Math.abs((entry.entryPrice ?? 0) - (initialStop ?? 0)) * this.priceMultiplier(entry);
  }

  private initialRiskTotal(entry: TradeEntry): number {
    const stored = Number(entry.initialRiskTotalEUR ?? 0);
    if (stored > 0) return +stored.toFixed(2);
    const riskInTradeCurrency = this.initialRiskPerUnit(entry) * (entry.size ?? 0);
    const riskInEuro = this.convertToEuro(riskInTradeCurrency, entry.currency);
    return +(riskInEuro + (entry.entryFee ?? 0)).toFixed(2);
  }

  private plannedR(entry: TradeEntry): number {
    const riskPerUnit = this.initialRiskPerUnit(entry);
    const rewardPerUnit = (this.calculationDirection(entry) === 'Long'
      ? (entry.targetPrice ?? 0) - (entry.entryPrice ?? 0)
      : (entry.entryPrice ?? 0) - (entry.targetPrice ?? 0)) * this.priceMultiplier(entry);
    if (riskPerUnit <= 0 || rewardPerUnit <= 0) return 0;
    return +(rewardPerUnit / riskPerUnit).toFixed(2);
  }

  private realizedR(entry: TradeEntry, pnl = this.realizedPnl(entry)): number {
    const risk = this.initialRiskTotal(entry);
    if (risk <= 0) return 0;
    const pnlInEuro = this.convertToEuro(pnl, entry.currency);
    return +(pnlInEuro / risk).toFixed(2);
  }

  private realizedDisplayR(entry: TradeEntry): number {
    const risk = this.initialRiskTotal(entry);
    if (risk <= 0) return 0;
    return +(this.displayPnl(entry) / risk).toFixed(2);
  }

  private partialRealizedR(entry: TradeEntry, sale: PartialSale): number {
    const riskPerUnit = this.initialRiskPerUnit(entry);
    const proportionalEntryFee = (entry.entryFee ?? 0) * (sale.size / (entry.size || sale.size || 1));
    const saleRisk = (riskPerUnit * sale.size) + proportionalEntryFee;
    if (saleRisk <= 0) return 0;
    return +(sale.pnl / saleRisk).toFixed(2);
  }

  private withLearningMetrics(entry: TradeEntry): TradeEntry {
    const initialStopPrice = entry.initialStopPrice ?? entry.stopPricePrevious ?? entry.stopPrice;
    const initialRiskPerUnit = this.initialRiskPerUnit({ ...entry, initialStopPrice });
    const initialRiskTotalEUR = this.initialRiskTotal({ ...entry, initialStopPrice });
    const realizedPnl = entry.status === 'Glatt' || (entry.partialSales?.length ?? 0) > 0
      ? this.realizedPnl(entry)
      : entry.realizedPnl ?? 0;
    const partialSales = (entry.partialSales ?? []).map((sale) => ({
      ...sale,
      realizedR: this.partialRealizedR({ ...entry, initialStopPrice, initialRiskTotalEUR }, sale),
    }));
    const reviewRequired = entry.status === 'Glatt' || entry.reviewRequired === true;
    const reviewCompleted = !!entry.reviewCompleted;

    return {
      ...entry,
      initialStopPrice,
      initialRiskPerUnit: +initialRiskPerUnit.toFixed(2),
      initialRiskTotalEUR,
      plannedR: this.plannedR({ ...entry, initialStopPrice }),
      realizedPnl,
      realizedR: this.realizedR({ ...entry, initialStopPrice, initialRiskTotalEUR }, realizedPnl),
      openRiskR: this.remainingSize(entry) > 0 && entry.status !== 'Glatt' ? 1 : 0,
      reviewRequired,
      reviewCompleted,
      partialSales,
    };
  }

  recalculatePositionAndProfit(trigger: 'entry' | 'stop' | 'target' | 'direction' | 'size' | 'risk' | 'init'): void {
    const sizeControl = this.tradeForm.get('size');
    const riskPerShare = this.riskPerShare();
    const budget = this.riskPerTradeEuro;

    const shouldUpdateSize = ['entry', 'stop', 'direction', 'risk', 'init'].includes(trigger);

    if (sizeControl && shouldUpdateSize) {
      const baseSize = riskPerShare > 0 ? Math.floor(budget / riskPerShare) : 0;
      const nextSize = this.adjustedPositionSize(baseSize);
      if (sizeControl.value !== nextSize) {
        sizeControl.setValue(nextSize, { emitEvent: false });
      }
    }

    this.synchronizeCfdEntryFee();
  }

  private normalizeEntry(entry: TradeEntry): TradeEntry {
    const screenshots = (entry.screenshots ?? [])
      .map((shot, idx) => ({ ...shot, indexOrder: typeof shot.indexOrder === 'number' ? shot.indexOrder : idx }))
      .sort((a, b) => a.indexOrder - b.indexOrder);
    const partialSales = (entry.partialSales ?? []).map((sale) => ({
      ...sale,
      fee: sale.fee ?? 0,
      pnl: this.calculateRealizedPnl(entry, sale.price, sale.size, sale.fee ?? 0),
      screenshots: sale.screenshots?.map((shot, idx) => ({
        ...shot,
        indexOrder: typeof shot.indexOrder === 'number' ? shot.indexOrder : idx,
        partialSaleId: shot.partialSaleId || sale.id,
      })) ?? screenshots.filter((shot) => this.shotSaleId(shot) === sale.id),
    }));
    const soldPieces = partialSales.reduce((sum, sale) => sum + sale.size, 0);
    const inferredStatus: TradeState = soldPieces >= (entry.size ?? 0)
      ? 'Glatt'
      : soldPieces > 0
        ? 'Teilverkauf'
        : entry.positioned
          ? 'Positioniert'
          : 'Watch';
    const status = this.normalizeStatus(entry.status, inferredStatus);
    const positioned = status === 'Watch' || status === 'Storniert'
      ? false
      : status === 'Glatt'
        ? false
        : entry.positioned ?? true;

    return this.withLearningMetrics({
      ...entry,
      feldTextId: entry.feldTextId ?? this.activeJournalArea?.feldTextId ?? this.defaultArea().feldTextId,
      wknIsin: entry.wknIsin || undefined,
      status,
      positioned,
      sellDate: entry.sellDate ?? '',
      sellPrice: entry.sellPrice ?? undefined,
      sellSize: entry.sellSize ?? entry.size,
      entryFee: entry.entryFee ?? 0,
      sellFee: entry.sellFee ?? 0,
      realizedPnl: entry.realizedPnl ?? 0,
      partialSales,
      screenshots,
      currency: entry.currency ?? 'EUR',
      certificate: !!entry.certificate,
      tradeType: entry.tradeType || (entry.certificate ? 'Zertifikat' : 'Aktie'),
      crvGrund: this.validPositiveNumber(entry.crvGrund) ?? 3,
      marge: this.validNumber(entry.marge) ?? 0,
      reviewRequired: entry.reviewRequired ?? status === 'Glatt',
      reviewCompleted: !!entry.reviewCompleted,
    });
  }

  async applyAiPrompt(): Promise<void> {
    const text = (this.tradeForm.value.aiPrompt ?? '').toString().trim();
    const systemPrompt = (this.tradeForm.value.aiSystemPrompt ?? '').toString().trim();
    if (!text) {
      this.aiStatus = 'Bitte gib einen Text ein, der analysiert werden soll.';
      return;
    }

    this.aiStatus = 'Sende an ChatGPT…';
    let chatResponse = '';
    try {
      const result = await firstValueFrom(this.api.runPrompt(text, systemPrompt || text));
      chatResponse = (result?.content ?? '').toString().trim();
      this.aiStatus = chatResponse ? `ChatGPT-Antwort: ${chatResponse}` : 'ChatGPT-Antwort erhalten.';
    } catch (error) {
      this.showError('Auswertung konnte nicht durchgeführt werden.', error);
      this.aiStatus = this.formatError(error) || 'Auswertung fehlgeschlagen.';
      return;
    }
    const parseSource = chatResponse || text;
    const suggestion = await this.simulateChatGptExtraction(parseSource);

    this.tradeForm.patchValue(
      {
        direction: suggestion.direction ?? (this.tradeForm.value.direction as TradeDirection),
        setup: suggestion.setup ?? this.tradeForm.value.setup,
        entryPrice: suggestion.entryPrice ?? this.tradeForm.value.entryPrice,
        stopPrice: suggestion.stopPrice ?? this.tradeForm.value.stopPrice,
        targetPrice: suggestion.targetPrice ?? this.tradeForm.value.targetPrice,
      },
      { emitEvent: false }
    );

    this.recalculatePositionAndProfit('init');

    const autoSize = this.maxPositionSize();
    this.tradeForm.get('size')?.setValue(autoSize, { emitEvent: false });

    this.aiStatus = chatResponse ? `${chatResponse} – Werte übernommen.` : 'Werte übernommen.';
  }

  private async sendPromptToChatGpt(prompt: string, systemPrompt?: string): Promise<string> {
    return this.openAi.sendChatCompletion(prompt, systemPrompt);
  }

  private async simulateChatGptExtraction(text: string): Promise<{
    direction: TradeDirection | null;
    setup: string | null;
    entryPrice: number | null;
    stopPrice: number | null;
    targetPrice: number | null;
  }> {
    const normalized = text.replace(/\n+/g, ' ');
    const direction = this.extractDirection(normalized);
    const setup = this.extractSetup(normalized);
    const entryPrice = this.extractNumber(normalized, [/entry\s*[:=]?\s*([0-9]+[.,]?[0-9]*)/i, /einstieg\s*[:=]?\s*([0-9]+[.,]?[0-9]*)/i]);
    const stopPrice = this.extractNumber(normalized, [/stop(?:loss)?\s*[:=]?\s*([0-9]+[.,]?[0-9]*)/i, /stopp?\s*[:=]?\s*([0-9]+[.,]?[0-9]*)/i]);
    const targetPrice = this.extractNumber(normalized, [/ziel\s*[:=]?\s*([0-9]+[.,]?[0-9]*)/i, /target\s*[:=]?\s*([0-9]+[.,]?[0-9]*)/i]);

    const fallbackNumbers = (normalized.match(/[0-9]+(?:[.,][0-9]+)?/g) || []).map((n) => parseFloat(n.replace(',', '.')));

    return {
      direction,
      setup,
      entryPrice: entryPrice ?? fallbackNumbers[0] ?? null,
      stopPrice: stopPrice ?? fallbackNumbers[1] ?? null,
      targetPrice: targetPrice ?? fallbackNumbers[2] ?? null,
    };
  }

  private extractDirection(text: string): TradeDirection | null {
    const lower = text.toLowerCase();
    if (lower.includes('short')) return 'Short';
    if (lower.includes('long')) return 'Long';
    return null;
  }

  private extractSetup(text: string): string | null {
    const lower = text.toLowerCase();
    const match = this.setups.find((setup) => lower.includes(setup.toLowerCase()));
    return match ?? null;
  }

  private extractNumber(text: string, patterns: RegExp[]): number | null {
    for (const pattern of patterns) {
      const match = text.match(pattern);
      if (match?.[1]) {
        const value = parseFloat(match[1].replace(',', '.'));
        if (!isNaN(value)) {
          return value;
        }
      }
    }
    return null;
  }

  private setupRiskSummarySubscription(): void {
    this.subscriptions.add(
      this.riskBudget.summary$.subscribe((summary: RiskSummary) => {
        this.positionedPnLEuro = summary.positionedPnLEUR;
        this.totalRiskBudgetEuro = summary.totalRiskBudgetEUR;
        this.riskPerTradeEuro = summary.riskPerTradeEUR;
        this.riskRewardRatio = summary.profile.riskRewardRatio ?? DEFAULT_RISK_PROFILE.riskRewardRatio ?? 0;
        this.appliedRiskStep = summary.appliedStep;
        this.recalculatePositionAndProfit('init');
      })
    );
  }

  private updatePositionedPnl(): void {
    const pnl = this.entries()
      .filter((entry) => entry.status !== 'Watch')
      .reduce((sum, entry) => sum + this.displayPnl(entry), 0);
    this.positionedPnLEuro = +pnl.toFixed(2);
    this.riskBudget.updatePositionedPnl(this.positionedPnLEuro);
  }

  private async loadRiskProfile(): Promise<void> {
    const currentYear = new Date().getFullYear();
    let planYear = currentYear;

    try {
      const ids = await firstValueFrom(this.api.getRiskProfileIds());
      const years = (ids ?? [])
        .map((id) => Number(id))
        .filter((year) => Number.isFinite(year));
      if (years.length) {
        planYear = years.includes(currentYear) ? currentYear : Math.max(...years);
      }
    } catch {
      // keep default year
    }

    try {
      const profile = await firstValueFrom(this.api.getRiskProfile(planYear));
      if (!profile) {
        this.riskBudget.setProfile({ ...DEFAULT_RISK_PROFILE, planYear });
        return;
      }

      try {
        const steps = await firstValueFrom(this.api.getRiskSteps(profile.id ?? String(planYear)));
        if (steps?.length) {
          this.riskBudget.setProfile({ ...profile, riskSteps: steps });
          return;
        }
      } catch {
        // ignore step errors
      }

      this.riskBudget.setProfile(profile);
    } catch {
      this.riskBudget.setProfile({ ...DEFAULT_RISK_PROFILE, planYear });
    }
  }
}
